import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { MultiSelect } from 'react-multi-select-component';
import Swal from 'sweetalert2';
import classnames from 'classnames';
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Row,
  Col,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
  Label,
  Input,
  Button,
  UncontrolledTooltip,
} from 'reactstrap';

import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_blue.css';
import 'flatpickr/dist/plugins/monthSelect/style.css';
import monthSelectPlugin from 'flatpickr/dist/plugins/monthSelect';
import { Indonesian } from 'flatpickr/dist/l10n/id.js'; // untuk locale ID
import { incoTermTable } from '../../common/data';
import { isNumericOnly } from '../../utils/validators';
import logger from '../../utils/logger';
import axios from 'axios';
import { api } from '../../config';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Select from 'react-select';
import { format } from 'date-fns';
import { convertToDisplayDateFormatddmmmyyyy } from '../../utils/dateformatter';
import {
  convertToInputDateFormat,
  convertToDisplayDateFormat,
  formatDateToDDMMYYYY,
  convertDisplayddMMyyyy,
  getSafeInputDate,
} from '../../utils/dateformatter';
import DOMPurify from 'dompurify';
import { formatNumber, formatNumberUS } from '../../utils/numberformatter';

import {
  PricingPeriodData,
  IncotermPortTypeData,
  IncotermMultiPortTypeData,
  CargoType,
  PartyType,
} from '../../constants/masterData';

import DeliveryVesselFirstLocation from './component/DeliveryVesselFirstLocation';
import DeliveryVesselSecondLocation from './component/DeliveryVesselSecondLocation';
import DeliveryVesselFirstLocationNotes from './component/DeliveryVesselFirstLocationNotes.js';
import CargoTypeComposition from './component/CargoTypeComposition';
import ViewOriginModal from './component/ViewOriginModal';
import Composition from './component/Composition';

import {
  BtnBold,
  BtnItalic,
  Editor,
  EditorProvider,
  Toolbar,
  BtnBulletList,
  BtnClearFormatting,
  BtnLink,
  BtnNumberedList,
  BtnRedo,
  BtnStrikeThrough,
  BtnStyles,
  BtnUnderline,
  BtnUndo,
  Separator,
} from 'react-simple-wysiwyg';
import IncotermTableFragment from './component/IncotermTableFragment';
import QuantityTableFragment from './component/QuantityTableFragment';
import '../../App.css';
import { getAuthToken } from '../../auth/tokenvalidation';
import apiClient from '../../config/apiClient';
axios.defaults.withCredentials = true;
axios.defaults.xsrfCookieName = 'XSRF-TOKEN';
axios.defaults.xsrfHeaderName = 'X-XSRF-TOKEN';

const TARGET_PRODUCT_TYPES_MIX = [
  '4588b5e6-7309-49d0-9107-59cbb96b6b18',
  '4588b5e6-7309-49d0-9107-59cbb96b6b19',
];

const LPG_PRODUCT = ['4588b5e6-7309-49d0-9107-59cbb96b6bd8'];

const REQ_SECTIONS = [
  { id: 'req-sec-contract', label: 'Contract', icon: 'ri-file-list-2-line' },
  { id: 'req-sec-product', label: 'Product Spec', icon: 'ri-drop-line' },
  {
    id: 'req-sec-composition',
    label: 'Composition',
    icon: 'ri-test-tube-line',
  },
  {
    id: 'req-sec-delivery',
    label: 'Quantity & Delivery',
    icon: 'ri-truck-line',
  },
  { id: 'req-sec-port', label: 'Port Restriction', icon: 'ri-anchor-line' },
  { id: 'req-sec-clause', label: 'Clause Details', icon: 'ri-article-line' },
  {
    id: 'req-sec-payment',
    label: 'Payment Terms',
    icon: 'ri-bank-card-line',
  },
  {
    id: 'req-sec-compliance',
    label: 'Compliance',
    icon: 'ri-shield-check-line',
  },
  {
    id: 'req-sec-other',
    label: 'Other Terms',
    icon: 'ri-file-settings-line',
  },
  {
    id: 'req-sec-validity',
    label: 'Validity',
    icon: 'ri-shield-check-line',
  },
];

const isFilledLocation = value =>
  value !== null && value !== undefined && String(value).trim() !== '';

// Total lokasi di seluruh baris incoterm:
// - fob      : jumlah entri incotermFOBLocations
// - location : jumlah incotermLocation / incotermLocation2 / incotermLocation3 yang terisi
const countIncotermLocations = (items = []) =>
  items.reduce(
    (totals, data) => ({
      fob:
        totals.fob +
        (Array.isArray(data.incotermFOBLocations)
          ? data.incotermFOBLocations.length
          : 0),
      location:
        totals.location +
        [
          data.incotermLocation,
          data.incotermLocation2,
          data.incotermLocation3,
        ].filter(isFilledLocation).length,
    }),
    { fob: 0, location: 0 }
  );

const FOB_INCOTERM_ID = 'e0173dfd-e109-4054-8640-23d95befd326';
const MAX_INCOTERM_LOCATIONS = 3;

// Nilai lokasi bisa berupa array (FOB / MultiPort) atau nilai tunggal.
const countLocationValue = value =>
  Array.isArray(value)
    ? value.filter(isFilledLocation).length
    : isFilledLocation(value)
      ? 1
      : 0;

// Jumlah lokasi pada form yang sedang diisi. Hanya dipakai untuk incoterm
// non-FOB — penambahan lokasi lewat MultiSelect incotermFOB tidak dibatasi.
const countFormIncotermLocations = (data = {}) =>
  countLocationValue(data.incotermLocationId) +
  countLocationValue(data.incotermLocation2Id) +
  countLocationValue(data.incotermLocation3Id);

// Satu nama port cukup tampil sekali di tabel Port Restriction.
// Perbandingan mengabaikan huruf besar/kecil dan spasi di tepi,
// jadi "Tanjung Priok" dan "tanjung priok " dianggap sama.
const dedupePortsByName = ports => {
  const seen = new Set();
  return (Array.isArray(ports) ? ports : []).filter(port => {
    const key = String(port?.name ?? '')
      .trim()
      .toLowerCase();
    if (!key) return true; // entri tanpa nama dibiarkan tampil, bukan disembunyikan
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const RequirementTab = ({
  _goNext,
  _ids,
  form,
  setForm,
  dataCause,
  setDataCause,
  handleSaveDraftProc,
  handleSaveCompleteProc,
  validatedeliveryModal,
  errors,
  setErrors,
  setIsLoading,
  isLoading,
  _completedMode,
}) => {
  const quantityRefs = useRef();
  const isendDate = form.contractType
    ? parseInt(form.contractType, 10) !== 1
    : false;
  const cargoTypeOptions = CargoType[0].options.filter(opt => {
    if (opt.value === 1) return form.isFullCargoVLGC;
    if (opt.value === 2) return form.isHalfCargoVLGC;
    if (opt.value === 3) return form.isSemiRefrigerated;
    return true;
  });
  const [unitSizeType, setUnitSizeType] = useState('');
  const [deletemodal, setDeleteModal] = useState(false);
  const [modal, setModal] = useState(false);
  const [modalincoterm, setModalIncoterm] = useState(false);
  const [modalquantity, setModalQuantity] = useState(false);
  const [modalloadingdischarge, setModalLoadingDischarge] = useState(false);
  const [modalquantityincoterm, setModalQuantityIncoterm] = useState(false);
  const [productName, setProductName] = useState({ items: [] });
  const [productTypeData, setProductTypeData] = useState({ items: [] });
  const [currencyName, setCurrencyName] = useState({ items: [] });
  const [, setRatioData] = useState({ items: [] });
  const [invoiceQuantityData, setInvoiceQuantityData] = useState({ items: [] });
  const [modelinvoiceQuantityData, setModelInvoiceQuantityData] = useState({
    items: [],
  });
  const [qtyDeliveryData, setQtyDeliveryData] = useState({ items: [] });
  const [qtyDeliveryDataQuantity, setQtyDeliveryDataQuantity] = useState({
    items: [],
  });

  const [qtyDeliveryDataTotalQty, setQtyDeliveryDataTotalQty] = useState({
    items: [],
  });

  const [IncotermRouteData, setIncotermRouteData] = useState({ items: [] });
  const [IncotermLocationData, setIncoTermLocationData] = useState({
    items: [],
  });
  //const [setIsLoadingIncotermLoc] = useState(false);
  const [lawData, setLawData] = useState({ items: [] });
  const [abitrationData, setAbitrationData] = useState({ items: [] });
  const [dataindexprice, setDataIndexPrice] = useState({ items: [] });
  const [incotermtype, setIncotremType] = useState({ items: [] });
  const [qtyDeliveryDataIncoterm, setQtyDeliveryDataIncoterm] = useState({
    items: [],
  });
  const [viewOriginModal, setViewOriginModal] = useState(false);
  const [viewOriginLocations, setViewOriginLocations] = useState([]);
  const [viewOriginRoutes, setViewOriginRoutes] = useState('');
  const incotermLocationTotals = countIncotermLocations(
    qtyDeliveryDataIncoterm.items
  );
  const uniquePortRestrictions = dedupePortsByName(form.portRestriction);
  // Joined string, bukan array: `uniquePortRestrictions` adalah array baru tiap
  // render, jadi memakainya langsung sebagai dependency membuat effect di bawah
  // jalan terus-menerus.
  const portRestrictionIdsKey = uniquePortRestrictions
    .map(item => item.locationId)
    .filter(Boolean)
    .join(',');
  const token = getAuthToken();
  const [error, setError] = useState(null);
  const editorRef = useRef(null);
  const [insertOption, setInsertOption] = useState('');
  const [templateOptions, setTemplateOptions] = useState([]);
  const [templatesByClauseDetailId, setTemplatesByClauseDetailId] = useState(
    {}
  );
  const [activeTab, setactiveTab] = useState('1');
  const toggles = tab => {
    if (activeTab !== tab) {
      setactiveTab(tab);
    }
  };

  const [activeTabs, setActiveTabs] = useState({});
  const toggle = (id, tab) => {
    setActiveTabs(prev => ({
      ...prev,
      [id]: tab,
    }));
  };

  const [jettyRestrictions, setJettyRestrictions] = useState([]);
  const [jettyRestrictionsLoading, setJettyRestrictionsLoading] =
    useState(false);

  useEffect(() => {
    const ids = portRestrictionIdsKey ? portRestrictionIdsKey.split(',') : [];
    if (ids.length === 0) {
      setJettyRestrictions([]);
      return;
    }

    let cancelled = false;
    const loadJettyRestrictions = async () => {
      setJettyRestrictionsLoading(true);
      try {
        const params = new URLSearchParams();
        // API menerima satu key berisi id dipisah koma, bukan key berulang.
        params.append('LocationIds', ids.join(','));
        if (form.productNameId) {
          params.append('ProductId', form.productNameId);
        }

        const response = await axios.get(
          `${api.API_URL}/location-jetty/list?${params.toString()}`,
          {
            withXSRFToken: true,
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const result = response?.data ?? response;
        const items =
          result?.data?.items || result?.items || result?.data || result || [];
        if (!cancelled) {
          setJettyRestrictions(Array.isArray(items) ? items : []);
        }
      } catch (error) {
        if (!cancelled) {
          logger.error('Error fetching incoterm location jetty:', error);
          setJettyRestrictions([]);
        }
      } finally {
        if (!cancelled) {
          setJettyRestrictionsLoading(false);
        }
      }
    };

    loadJettyRestrictions();
    // Cegah response lama menimpa response baru saat produk diganti cepat.
    return () => {
      cancelled = true;
    };
  }, [portRestrictionIdsKey, form.productNameId, token]);

  const groupedJettyRestrictions = useMemo(() => {
    const groups = new Map();
    jettyRestrictions.forEach(item => {
      const key = item.locationId ?? item.locationName ?? '';
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          name: item.locationName || '-',
          items: [],
        });
      }
      groups.get(key).items.push(item);
    });
    return Array.from(groups.values());
  }, [jettyRestrictions]);

  const [propanesection, setPropaneSection] = useState(false);
  const [butanesection, setButaneSection] = useState(false);
  const [comptitle, setCompTile] = useState('');
  const [sectitle, setSecTile] = useState('');
  const [incotermErrors, setIncotermErrors] = useState({});

  const handleNumberChange = fieldName => e => {
    const input = e.target.value;
    if (isNumericOnly(input)) {
      setForm(prev => ({
        ...prev,
        [fieldName]: input === '' ? null : parseInt(input),
      }));
    }
  };

  const handleNoteTextChange = (event, field) => {
    setForm(form => ({
      ...form,
      [field]: event.target.value,
    }));
  };

  const handleNoteTextChanges = (event, field) => {
    const itemId = field.replace('clauseDesc_', '');
    const clauseDetail = dataCause?.procurementClauseDetails?.find(
      d => String(d.id) === String(itemId)
    );
    const fallbackItem =
      dataCause?.items?.find(i => String(i.id) === String(itemId)) || {};
    const originalItem = clauseDetail || fallbackItem;
    setDataCause(dataCause => {
      const updatedItem = {
        ...originalItem,
        id: String(itemId),
        clauseDetailId: originalItem.clauseDetailId ?? null,
        clauseDescription: event.target.value,
        isShowToDMUT: originalItem.isShowToDMUT ?? null,
      };
      const prev = dataCause.procurementClauseDetails ?? dataCause?.items ?? [];
      const idx = prev.findIndex(i => String(i.id) === String(itemId));
      const procurementClauseDetails =
        idx >= 0
          ? prev.map((i, n) => (n === idx ? { ...i, ...updatedItem } : i))
          : [...prev, updatedItem];
      const rest = { ...dataCause };
      delete rest[itemId];
      delete rest[field];
      return {
        ...rest,
        procurementClauseDetails,
      };
    });
  };

  const handleDropdownInsert = (value, editorId) => {
    if (!value) return;
    const tpl = templateOptions.find(t => String(t.id) === String(value));
    if (!tpl || !tpl.template) return;

    const sanitizedHtml = DOMPurify.sanitize(tpl.template, {
      ALLOWED_TAGS: [
        'b',
        'i',
        'u',
        's',
        'strong',
        'em',
        'p',
        'br',
        'ul',
        'ol',
        'li',
        'span',
        'div',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'a',
        'sub',
        'sup',
      ],
      ALLOWED_ATTR: ['href', 'target', 'class', 'style'],
    });

    const editorEl = editorId
      ? document.getElementById(editorId)
      : editorRef.current;
    if (editorEl) {
      editorEl.focus();
      //document.execCommand('insertHTML', false, tpl.template);
      //handleNoteTextChange({ target: { value: editorEl.innerHTML } }, editorId);
      document.execCommand('insertHTML', false, sanitizedHtml);
      const cleanOutput = DOMPurify.sanitize(editorEl.innerHTML);
      handleNoteTextChange({ target: { value: cleanOutput } }, editorId);
    }
    setInsertOption('');
  };

  const handleTextChangeDelivery = field => event => {
    setDeliveryFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleDeliveryTextChange = e => {
    const { name, value } = e.target;
    setDeliveryFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDeliveryDateChange = e => {
    const { name, value } = e.target;
    setDeliveryFormData(prev => ({
      ...prev,
      [name]: convertToDisplayDateFormat(value),
    }));
  };

  const validatePublishIndexDates = (startDate, endDate) => {
    if (!startDate || !endDate) return null;
    const [sDay, sMonth, sYear] = startDate.split('/').map(Number);
    const [eDay, eMonth, eYear] = endDate.split('/').map(Number);
    const start = new Date(sYear, sMonth - 1, sDay);
    const end = new Date(eYear, eMonth - 1, eDay);
    if (end < start) {
      return 'End Date cannot be earlier than Start Date';
    }
    return null;
  };

  const validatePublishIndexDatesOnSave = () => {
    if (
      deliveryformData.pricingPeriod === 1 &&
      deliveryformData.startDatePublishIndex &&
      deliveryformData.endDatePublishIndex
    ) {
      const err = validatePublishIndexDates(
        deliveryformData.startDatePublishIndex,
        deliveryformData.endDatePublishIndex
      );
      if (err) {
        setErrors(prev => ({ ...prev, endDatePublishIndex: err }));
        toast.error('Please Check Mandatory Fields..', {
          position: 'top-right',
          hideProgressBar: false,
          closeOnClick: false,
          className: 'bg-warning text-black',
        });
        return false;
      }
    }
    if (!validateProposalDates()) return false;
    return true;
  };

  const validateProposalDates = () => {
    const { proposalSubmissionDeadline, proposalValidityPeriodMax } = form;
    if (!proposalSubmissionDeadline || !proposalValidityPeriodMax) return true;

    const parseDate = d => {
      if (d instanceof Date) return d;
      if (typeof d === 'string') {
        const [datePart, timePart] = d.split(' ');
        const [day, month, year] = datePart.split('/').map(Number);
        const [hours, minutes] = (timePart || '0:0').split(':').map(Number);
        return new Date(year, month - 1, day, hours, minutes);
      }
      return new Date(d);
    };

    const submission = parseDate(proposalSubmissionDeadline);
    const validity = parseDate(proposalValidityPeriodMax);

    if (validity < submission) {
      setErrors(prev => ({
        ...prev,
        proposalValidityPeriodMax:
          'Validity Period cannot be earlier than Submission Deadline',
      }));
      toast.error(
        'Validity Period cannot be earlier than Submission Deadline',
        {
          position: 'top-right',
          hideProgressBar: false,
          closeOnClick: false,
          className: 'bg-warning text-black',
        }
      );
      return false;
    }
    return true;
  };

  // const handleDeliverySelectChange = selectedOption => {
  //   setDeliveryFormData(prev => ({
  //     ...prev,
  //     ratioLpgId: selectedOption.value,
  //   }));
  // };

  const handleDeliverySelectChange = (e, field) => {
    const value = e ? e.value : '';
    setDeliveryFormData(prev => ({ ...prev, [field]: value }));
  };

  const validatequantityanddelivery = () => {
    if (
      !qtyDeliveryDataIncoterm.items ||
      qtyDeliveryDataIncoterm.items.length === 0
    ) {
      return true;
    }

    let tempErrors = {};
    if (!deliveryformData.deliveryUnit?.trim()) {
      tempErrors.deliveryUnit = 'Please enter Delivery Unit';
    }

    if (!deliveryformData.pricingPeriod) {
      tempErrors.pricingPeriod = 'Pricing Period wajib diisi.';
    }

    if (
      deliveryformData.pricingPeriod !== 3 &&
      !deliveryformData.indexPriceId
    ) {
      tempErrors.indexPriceId = 'Index Price Reference wajib diisi.';
    }

    if (
      deliveryformData.pricingPeriod !== 3 &&
      !deliveryformData.otherIndexPriceId
    ) {
      tempErrors.otherIndexPriceId = 'Other Index Price wajib diisi.';
    }

    if (deliveryformData.pricingPeriod === 1) {
      if (!deliveryformData.startDatePublishIndex) {
        tempErrors.startDatePublishIndex = 'Start Date wajib diisi.';
      }
      if (!deliveryformData.endDatePublishIndex) {
        tempErrors.endDatePublishIndex = 'End Date wajib diisi.';
      }
      if (
        deliveryformData.startDatePublishIndex &&
        deliveryformData.endDatePublishIndex
      ) {
        const err = validatePublishIndexDates(
          deliveryformData.startDatePublishIndex,
          deliveryformData.endDatePublishIndex
        );
        if (err) {
          tempErrors.endDatePublishIndex = err;
        }
      }
    }

    if (deliveryformData.pricingPeriod === 2) {
      if (!deliveryformData.schemaBL?.trim()) {
        tempErrors.schemaBL = 'Schema B/L Date wajib diisi.';
      }
    }

    if (Object.keys(tempErrors).length !== 0) {
      toast.error('Please Check Mandatory Fields..', {
        position: 'top-right',
        hideProgressBar: false,
        closeOnClick: false,
        className: 'bg-warning  text-black',
      });
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const toggledeletemodal = () => {
    setDeleteModal(!deletemodal);
  };

  const deliverymodal = () => {
    if (validatedeliveryModal()) {
      setModal(!modal);
    }
  };

  const togglemodalincoterm = () => {
    setIncotermErrors({});
    setModalIncoterm(!modalincoterm);
  };

  const quantitymodal = () => {
    setModalQuantity(!modalquantity);
  };

  const togglemodalquantity = () => {
    setModalQuantity(!modalquantity);
  };

  const toggleloadingdischrge = () => {
    setModalLoadingDischarge(!modalloadingdischarge);
  };

  const handleAddDataDelivery = async _e => {
    if (validatequantityanddelivery()) {
      try {
        setIsLoading(true);
        const response = await axios.post(
          `${api.API_URL}/quantity-delivery`,
          selectedFieldsDeliveryFormData,
          {
            withXSRFToken: true,
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        fetchDeliveryDataDetail(response.id);
        fetchDeliveryDataDetailIncoterm(response.id);
        //fetchDeliveryDataDetailQuantity(response.id);
      } catch (error) {
        logger.error('Error handleAddDataDelivery: ', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
        handleSaveDraftProc(); // tetap jalan
        LoadDefault();
      }
    }
  };

  const handleUpdDataDelivery = async _e => {
    if (!validatequantityanddelivery()) return;
    try {
      setIsLoading(true);
      await axios.put(
        `${api.API_URL}/quantity-delivery/${deliveryformData.id}`,
        selectedFieldsDeliveryFormData,
        {
          withXSRFToken: true,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      fetchDeliveryDataDetail(deliveryformData.id);
    } catch (error) {
      logger.error('Error handleUpdDataDelivery: ', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
      handleSaveDraftProc();
      setModal(!modal);
      LoadDefault();
    }
  };

  const handleTypeChange = e => {
    const contractTypeValue = e.target.value;
    const flags = parseInt(contractTypeValue);
    setForm(prev => ({
      ...prev,
      contractType: contractTypeValue,
      contractPeriodTermEnd: flags !== 1 ? null : prev.contractPeriodTermEnd,
    }));
  };

  useEffect(() => {
    quantityRefs.current = qtyDeliveryData.items.map(() => React.createRef());
  }, [qtyDeliveryData.items]);

  const fetchAbitration = useCallback(async () => {
    try {
      const response = await axios.get(
        `${api.API_URL}/law/getAll-arbitration`,
        {
          withXSRFToken: true,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const items = response.items.map(item => ({
        value: item.id,
        label: item.name,
      }));
      setAbitrationData({ items });
    } catch (error) {
      logger.error('Error:', error);
    }
  }, [token]);

  const fetchQtyDeliveryList = useCallback(async () => {
    if (!form?.id) {
      return;
    }
    setError(null);
    try {
      const response = await axios.get(
        `${api.API_URL}/quantity-delivery?ProcurementId=${form.id}`,
        {
          withXSRFToken: true,
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.items) {
        logger.warn('No items found in response');
        return;
      }

      const items = response.items.map(item => ({
        procurementId: item.procurementId,
        ratioLpgId: item.ratioLpgId,
        ratioName: item.ratioLpg?.ratioName ?? null,
        deliveryUnit: item.deliveryUnit,
        frequency: item.frequency,
        pricingPeriod: item.pricingPeriod,
        indexPricename: item.indexPrice?.name ?? null,
        otherIndexPricename: item.otherIndexPrice?.name ?? null,
        startDatePublishIndex: item.startDatePublishIndex,
        endDatePublishIndex: item.endDatePublishIndex,
        schemaBL: item.schemaBL,
        priceAmount: item.priceAmount,
        notesForPricing: item.notesForPricing,
        isCorrelationBetweenTwoIndex: item.isCorrelationBetweenTwoIndex,
        id: item.id,
        cargoType: item.cargoType,
      }));

      setQtyDeliveryData({ items });
    } catch (error) {
      setError(error.message);
    }
  }, [form?.id, token, setError]);

  useEffect(() => {
    const fetchProductName = async () => {
      try {
        const response = await apiClient.get(`/product`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const items = response.data.data.items.map(item => ({
          value: item.id,
          label: item.name,
        }));
        setProductName({ items });
      } catch (error) {
        logger.error('Error:', error);
      }
    };

    const fetchCurrencyName = async () => {
      try {
        const response = await apiClient.get(`/paymentcurrency`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const items = response.data.data.items.map(item => ({
          value: item.id,
          label: item.currencyName + ' (' + item.currencyCode + ')',
        }));
        setCurrencyName({ items });
      } catch (error) {
        logger.error('Error:', error);
      }
    };

    const fetchRatio = async () => {
      try {
        const response = await apiClient.get(`/ratio-lpg`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const items = response.data.data.items.map(item => ({
          value: item.id,
          label: item.ratioName,
        }));
        setRatioData({ items });
      } catch (error) {
        logger.error('Error:', error);
      }
    };

    const fetchLaw = async () => {
      try {
        const response = await axios.get(`${api.API_URL}/law`, {
          withXSRFToken: true,
          headers: { Authorization: `Bearer ${token}` },
        });
        const items = response.items.map(item => ({
          value: item.id,
          label: item.name,
        }));
        setLawData({ items });
      } catch (error) {
        logger.error('Error:', error);
      }
    };

    const fetchInvoiceQuantity = async () => {
      try {
        const response = await axios.get(`${api.API_URL}/invoicing-quantity`, {
          withXSRFToken: true,
          headers: { Authorization: `Bearer ${token}` },
        });
        const items = response.items.map(item => ({
          value: item.id,
          label: item.name,
        }));
        setInvoiceQuantityData({ items });
      } catch (error) {
        logger.error('Error:', error);
      }
    };

    const fetchIndexPrice = async () => {
      try {
        const response = await axios.get(`${api.API_URL}/indexprice`, {
          withXSRFToken: true,
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = response.items || [];
        const items = list.map(item => ({
          value: item.id,
          label: item.name,
        }));
        setDataIndexPrice({ items });
      } catch (error) {
        logger.error('Error:', error);
      }
    };

    const fetchIncotermRoute = async () => {
      try {
        const response = await axios.get(
          `${api.API_URL}/incoterm/get-incoterm-route`,
          {
            withXSRFToken: true,
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const items = response.items.map(item => ({
          value: item.id,
          label: item.name,
        }));
        setIncotermRouteData({ items });
      } catch (error) {
        logger.error('Error:', error);
      }
    };

    const fetchIncotermType = async () => {
      try {
        const response = await axios.get(`${api.API_URL}/incoterm`, {
          withXSRFToken: true,
          headers: { Authorization: `Bearer ${token}` },
        });
        const items = response.items.map(item => ({
          value: item.id,
          label: item.name,
        }));
        setIncotremType({ items });
      } catch (error) {
        logger.error('Error index price:', error);
      }
    };

    const fetchTemplateData = async (clauseDetailId = null) => {
      try {
        let url = `${api.API_URL}/template-procurement-document?PageNumber=1&PageSize=50`;
        if (clauseDetailId) {
          url += `&ClauseDetailId=${clauseDetailId}`;
        }
        const response = await axios.get(url, {
          withXSRFToken: true,
          headers: { Authorization: `Bearer ${token}` },
        });
        const items = response.items || [];
        if (clauseDetailId) {
          setTemplatesByClauseDetailId(prev => ({
            ...prev,
            [clauseDetailId]: items,
          }));
        } else {
          setTemplateOptions(items);
        }
        return items;
      } catch (error) {
        logger.error('Error fetchTemplateData:', error);
        return [];
      }
    };

    const loadAll = async () => {
      try {
        setIsLoading(true);
        await Promise.all([
          fetchProductName(),
          fetchCurrencyName(),
          fetchRatio(),
          fetchInvoiceQuantity(),
          fetchLaw(),
          fetchIndexPrice(),
          fetchQtyDeliveryList(),
          fetchIncotermRoute(),
          fetchIncotermType(),
          fetchTemplateData(),
        ]);

        setDeliveryFormData(prev => ({
          ...prev,
          procurementId: form.id,
        }));
      } catch (error) {
        logger.error('Error fetching data: ', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAll();
  }, [form.id, fetchQtyDeliveryList, token, setIsLoading]);

  useEffect(() => {
    if (productTypeData.items?.length > 0 && form.productTypeId) {
      const found = productTypeData.items.find(
        item => item.value === form.productTypeId
      );
      if (found) {
        setUnitSizeType(found.uom);
      }
    }
  }, [productTypeData, form.productTypeId]);

  const fetchModeInvoiceQuantity = useCallback(async () => {
    try {
      const response = await axios.get(
        `${api.API_URL}/invoicing-quantity/getAll-ModeInvoicingQuantity`,
        {
          withXSRFToken: true,
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const items = response.items.map(item => ({
        value: item.id,
        label: item.name,
      }));
      setModelInvoiceQuantityData({ items });
    } catch (error) {
      logger.error('Error:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadfetchModeInvoiceQuantity = async () => {
      try {
        if (form.invoicingQuantityId) {
          await Promise.all([fetchModeInvoiceQuantity()]);
        }
      } catch (error) {
        logger.error('Error fetching data: ', error);
      }
    };
    loadfetchModeInvoiceQuantity();
  }, [form.invoicingQuantityId, fetchModeInvoiceQuantity]);

  useEffect(() => {
    const loadfetchAbitration = async () => {
      try {
        if (form.lawId) {
          await Promise.all([fetchAbitration()]);
        }
      } catch (error) {
        logger.error('Error fetching data: ', error);
      }
    };
    loadfetchAbitration();
  }, [form.lawId, fetchAbitration]);

  const fetchProductType = useCallback(
    async productTypeId => {
      try {
        const response = await axios.get(
          `${api.API_URL}/product-type?ProductId=${productTypeId}`,
          {
            withXSRFToken: true,
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const items = response.items.map(item => ({
          value: item.id,
          label: item.name,
          uom: item.uoM,
          pricingunit: item.pricingUnit,
        }));
        setProductTypeData({ items });
      } catch (error) {
        logger.error('Error:', error);
      }
    },
    [token]
  );

  useEffect(() => {
    if (form.productNameId) {
      fetchProductType(form.productNameId);
    }
  }, [form.productNameId, fetchProductType]);

  // useEffect(() => {
  //   fetchTemplateData();
  // });

  const LoadDefault = async () => {
    try {
      const promises = [fetchQtyDeliveryList()];

      // if (deliveryformData?.id) {
      //   promises.push;
      //fetchDeliveryDataDetailQuantity(deliveryformData.id),
      fetchDeliveryDataDetailIncoterm(deliveryformData.id);
      //}
      await Promise.all(promises);
      setDeliveryFormData(prev => ({
        ...prev,
        procurementId: form.id,
      }));
    } catch (error) {
      logger.error('LoadDefault error:', error);
    }
  };

  const handleSelectChangeDelivery = (e, field) => {
    setDeliveryFormData(prev => ({
      ...prev,
      [field]: e ? e.value : '',
      ...(field === 'otherIndexPriceId'
        ? { isCorrelationBetweenTwoIndex: e && e.value !== null }
        : {}),
      ...(field === 'pricingPeriod' && e?.value === 3
        ? {
            indexPriceId: null,
            otherIndexPriceId: null,
            schemaBL: null,
            startDatePublishIndex: null,
            endDatePublishIndex: null,
          }
        : {}),
    }));
  };

  const handleProductNameChange = selectedOption => {
    if (selectedOption) {
      setForm(prev => ({
        ...prev,
        productNameId: selectedOption.value,
        productTypeId: null,
      }));
    } else {
      setForm(prev => ({
        ...prev,
        productNameId: null,
        productTypeId: null,
      }));
      setUnitSizeType('');
    }
  };

  const handleProductTypeChange = selectedOption => {
    setForm(prev => ({
      ...prev,
      productTypeId: selectedOption.value,
      productType: selectedOption.label,
    }));
    setUnitSizeType(selectedOption.uom);
  };

  const handleSelectChange = (e, field) => {
    const value = e ? e.value : '';
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const [deliveryformData, setDeliveryFormData] = useState({
    procurementId: null,
    procurement: null,
    deliveryUnit: null,
    frequency: null,
    ratioLpgId: null,
    ratioLpg: null,
    pricingPeriod: null,
    indexPriceId: null,
    indexPrice: null,
    otherIndexPriceId: null,
    otherIndexPrice: null,
    isCorrelationBetweenTwoIndex: null,
    startDatePublishIndex: null,
    endDatePublishIndex: null,
    schemaBL: null,
    priceAmount: null,
    notesForPricing: null,
    id: null,
    cargoType: null,
  });

  const handleDeliveryOpenModal = (deliveryid = null) => {
    fetchDeliveryDataDetail(deliveryid);
    //fetchDeliveryDataDetailQuantity(deliveryid);
    fetchDeliveryDataDetailIncoterm(deliveryid);
    deliverymodal();
  };

  const generateQtyDeliveryTotalQtyByCargoType = useCallback(
    cargoType => {
      setQtyDeliveryDataTotalQty({ items: [] });

      if (TARGET_PRODUCT_TYPES_MIX.includes(form.productTypeId)) {
        let butaneMin, propaneMin, butaneMax, propaneMax;
        if (cargoType === 1) {
          butaneMin = form.fullCargoVLGCRefrigrateButaneMin;
          propaneMin = form.fullCargoVLGCRefrigratePropaneMin;
          butaneMax = form.fullCargoVLGCRefrigrateButaneMax;
          propaneMax = form.fullCargoVLGCRefrigratePropaneMax;
        } else if (cargoType === 2) {
          butaneMin = form.halfCargoVLGCRefrigrateButaneMin;
          propaneMin = form.halfCargoVLGCRefrigratePropaneMin;
          butaneMax = form.halfCargoVLGCRefrigrateButaneMax;
          propaneMax = form.halfCargoVLGCRefrigratePropaneMax;
        } else if (cargoType === 3) {
          butaneMin = form.semiRefrigeratedRefrigrateButaneMin;
          propaneMin = form.semiRefrigeratedRefrigratePropaneMin;
          butaneMax = form.semiRefrigeratedRefrigrateButaneMax;
          propaneMax = form.semiRefrigeratedRefrigratePropaneMax;
        }

        // if (cargoType === 3) {
        //   setQtyDeliveryDataTotalQty({ items: [] });
        //   return;
        // }

        if (
          butaneMin != null &&
          propaneMin != null &&
          butaneMax != null &&
          propaneMax != null
        ) {
          const maxTotal = Number(butaneMax) + Number(propaneMax);
          const step = cargoType === 1 ? 500 : 250;
          const items = [];
          let currentButane = Number(butaneMin);
          let currentPropane = Number(propaneMin);
          while (currentButane + currentPropane <= maxTotal) {
            items.push({
              quantity: currentButane + currentPropane,
              id: crypto.randomUUID(),
            });
            currentButane += step;
            currentPropane += step;
          }
          setQtyDeliveryDataTotalQty({ items });
        }
      } else {
        const compositionMin = form.compositionMin;
        const compositionMax = form.compositionMax;
        if (compositionMin != null && compositionMax != null) {
          const items = [];
          let current = Number(compositionMin);
          const max = Number(compositionMax);
          while (current <= max) {
            items.push({
              quantity: current,
              id: crypto.randomUUID(),
            });
            current += 500;
          }
          setQtyDeliveryDataTotalQty({ items });
        }
      }
    },
    [form, setQtyDeliveryDataTotalQty]
  );

  const handleDeliveryOpenModalNew = (_deliveryid = null) => {
    setDeliveryFormData({
      procurementId: null,
      procurement: null,
      deliveryUnit: null,
      frequency: null,
      ratioLpgId: null,
      ratioLpg: null,
      pricingPeriod: null,
      indexPriceId: null,
      indexPrice: null,
      otherIndexPriceId: null,
      otherIndexPrice: null,
      isCorrelationBetweenTwoIndex: null,
      startDatePublishIndex: null,
      endDatePublishIndex: null,
      schemaBL: null,
      priceAmount: null,
      notesForPricing: null,
      id: null,
      cargoType: null,
    });
    setQtyDeliveryDataIncoterm({ items: [] });
    if (TARGET_PRODUCT_TYPES_MIX.includes(form.productTypeId)) {
      setQtyDeliveryDataTotalQty({ items: [] });
    } else {
      generateQtyDeliveryTotalQtyByCargoType(form.cargoType);
    }
    deliverymodal();
  };

  const fetchDeliveryDataDetail = async deliveryid => {
    try {
      const response = await axios.get(
        `${api.API_URL}/quantity-delivery/${deliveryid}`,
        {
          withXSRFToken: true,
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: `application/json`,
          },
        }
      );
      const data = response;
      setDeliveryFormData({
        procurementId: data.procurementId || null,
        procurement: data.procurement || null,
        deliveryUnit: data.deliveryUnit || null,
        frequency: data.frequency || null,
        ratioLpgId: data.ratioLpgId || null,
        ratioLpg: data.ratioLpg || null,
        pricingPeriod: data.pricingPeriod || null,
        indexPriceId: data.indexPriceId || null,
        indexPrice: data.indexPrice || null,
        otherIndexPriceId: data.otherIndexPriceId || null,
        otherIndexPrice: data.otherIndexPrice || null,
        isCorrelationBetweenTwoIndex: data.isCorrelationBetweenTwoIndex || null,
        startDatePublishIndex:
          format(data.startDatePublishIndex, 'dd/MM/yyyy') || null,
        endDatePublishIndex:
          format(data.endDatePublishIndex, 'dd/MM/yyyy') || null,
        schemaBL: data.schemaBL || null,
        priceAmount: data.priceAmount || null,
        notesForPricing: data.notesForPricing || null,
        id: data.id || null,
        cargoType: data.cargoType || null,
      });
      fetchDeliveryDataDetailTotalQty(deliveryid);
    } catch (error) {
      logger.error('Error fetching data from Invoice API:', error);
    }
  };

  const selectedFieldsDeliveryFormData = {
    procurementId: form.id,
    ratioLpgId: deliveryformData.ratioLpgId,
    deliveryUnit: deliveryformData.deliveryUnit,
    frequency: deliveryformData.frequency,
    pricingPeriod: deliveryformData?.pricingPeriod ?? 1,
    indexPriceId: deliveryformData.indexPriceId,
    otherIndexPriceId: deliveryformData.otherIndexPriceId,
    isCorrelationBetweenTwoIndex: deliveryformData.isCorrelationBetweenTwoIndex,
    // startDatePublishIndex: deliveryformData?.startDatePublishIndex
    //   ? convertToInputDateFormat(deliveryformData.startDatePublishIndex)
    //   : null,
    startDatePublishIndex: getSafeInputDate(
      deliveryformData?.startDatePublishIndex,
      convertToInputDateFormat
    ),
    // endDatePublishIndex: convertToInputDateFormat(
    //   deliveryformData.endDatePublishIndex
    // ),
    endDatePublishIndex: getSafeInputDate(
      deliveryformData?.endDatePublishIndex,
      convertToInputDateFormat
    ),
    schemaBL: deliveryformData.schemaBL,
    priceAmount: deliveryformData.priceAmount,
    notesForPricing: deliveryformData.notesForPricing,
    cargoType: deliveryformData.cargoType,
    quantityInformations: qtyDeliveryDataTotalQty.items,
  };

  const handleDeleteQtyDelivery = (id = null) => {
    Swal.fire({
      title: `Are You sure want to Delete this Record? `,
      text: 'Deleted Data cannot be Recovered!!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, Delete!',
      cancelButtonText: 'Cancel',
    }).then(result => {
      if (result.isConfirmed) {
        // onDelete(); // Panggil props atau function untuk hapus data
        // fetchData(id);
        handleDelDataDelivery(id);
        //Swal.fire("Deleted!", "Data was successfully deleted.", "success");
      }
    });
  };

  const handleDeleteIncotermDeliveryDetail = (
    id = null,
    _deliveryid = null,
    _section = null
  ) => {
    Swal.fire({
      title: `Are You sure want to Delete this Record?`,
      text: 'Deleted Data cannot be Recovered!!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, Delete!',
      cancelButtonText: 'Cancel',
    }).then(result => {
      if (result.isConfirmed) {
        // onDelete(); // Panggil props atau function untuk hapus data
        handleDelDataDeliveryIncotermDetail(id);

        //Swal.fire("Deleted!", "Data was successfully deleted.", "success");
      }
    });
  };

  useEffect(() => {
    const newTabs = {};
    qtyDeliveryData.items.forEach(item => {
      newTabs[item.id] = `${item.id}1`;
    });
    setActiveTabs(newTabs);
  }, [qtyDeliveryData.items]);

  const [deliveryquantityformData, setDeliveryQuantityFormData] = useState({
    quantityDeliveryTermId: null,
    quantity: null,
    quantityLocation1: null,
    quantityLocation2: null,
    id: null,
  });

  const handleDeliveryQuantityOpenModalNew = (_deliveryid = null) => {
    setDeliveryQuantityFormData({
      quantityDeliveryTermId: null,
      quantity: null,
      quantityLocation1: null,
      quantityLocation2: null,
      id: null,
    });
    quantitymodal();
  };

  const selectedFieldsDeliveryQuantityUpdData = {
    quantityDeliveryTermId: deliveryformData.id,
    quantity: deliveryquantityformData.quantity,
    quantityLocation1: deliveryquantityformData.quantityLocation1,
    quantityLocation2: deliveryquantityformData.quantityLocation2,
  };

  const fetchDeliveryDataDetailIncoterm = async deliveryid => {
    try {
      const response = await axios.get(
        `${api.API_URL}/quantity-incoterm?QuantityDeliveryTermId=${deliveryid}`,
        {
          withXSRFToken: true,
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const items = response.items.map(item => ({
        incotermId: item.incotermId,
        incoterm: item.incoterm?.name ?? null,
        incotermPortType: item.incotermPortType,
        incotermLocation: item.incotermLocation?.location?.name ?? null,
        startDate: convertToDisplayDateFormatddmmmyyyy(item.startDate),
        endDate: convertToDisplayDateFormatddmmmyyyy(item.endDate),
        incotermLocation2: item.incotermLocation2?.location?.name ?? null,
        startDate2: convertToDisplayDateFormatddmmmyyyy(item.startDate2),
        endDate2: convertToDisplayDateFormatddmmmyyyy(item.endDate2),
        multiportOption: item.multiportOption,
        incotermFOBLocations: Array.isArray(item?.incotermFOBLocations)
          ? item.incotermFOBLocations
          : item?.incotermFOBLocations
            ? [item.incotermFOBLocations]
            : null,
        incotermLocation3: item.incotermLocation3?.location?.name ?? null,
        startDate3: convertToDisplayDateFormatddmmmyyyy(item.startDate3),
        endDate3: convertToDisplayDateFormatddmmmyyyy(item.endDate3),
        timelineType: item.timelineType,
        incotermRouteId: item.incotermRoute?.incotermId,
        id: item.id,
        routes: item.incotermRoute?.name ?? null,
      }));

      setQtyDeliveryDataIncoterm({ items });
    } catch (error) {
      logger.error('Error:', error);
    }
  };

  const handleUpdDataDeliveryQuantity = async deliveryqtyid => {
    try {
      setIsLoading(true);
      await axios.put(
        `${api.API_URL}/quantity-information/${deliveryqtyid}`,
        selectedFieldsDeliveryQuantityUpdData,
        {
          withXSRFToken: true,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success(`Data Has been Updated`);
      //fetchDeliveryDataDetailQuantity(deliveryformData.id);
    } catch (error) {
      logger.error('Error handleUpdDataDeliveryQuantity: ', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
      LoadDefault();
    }
  };

  const handleAddDataDeliveryQuantity = () => {
    const qty = deliveryquantityformData.quantity;
    if (qty == null || qty === '') return;
    const editIndex = deliveryquantityformData._editIndex;
    setQtyDeliveryDataTotalQty(prev => {
      if (editIndex != null) {
        const items = prev.items.map((item, i) =>
          i === editIndex ? { ...item, quantity: Number(qty) } : item
        );
        return { items };
      }
      return {
        items: [
          ...prev.items,
          { quantity: Number(qty), id: crypto.randomUUID() },
        ],
      };
    });
    setDeliveryQuantityFormData(prev => ({ ...prev, _editIndex: null }));
  };

  const handleDeleteQuantityItem = index => {
    setQtyDeliveryDataTotalQty(prev => ({
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const handleEditQuantityItem = index => {
    const item = qtyDeliveryDataTotalQty.items[index];
    setDeliveryQuantityFormData(prev => ({
      ...prev,
      quantity: item.quantity,
      _editIndex: index,
    }));
    quantitymodal();
  };

  const handleDelDataDelivery = async id => {
    try {
      setIsLoading(true);
      await axios.delete(`${api.API_URL}/quantity-delivery/${id}`, {
        withXSRFToken: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success(`Data has been deleted`);
    } catch (error) {
      logger.error('Error handleDelDataDelivery: ', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
      LoadDefault();
    }
  };

  const handleSelectChangeIncoterm = (e, field) => {
    const value = e ? e.value : '';
    setIncotermErrors(prev => ({ ...prev, [field]: null }));

    setDeliveryIncotermFormData(prev => {
      const newData = { ...prev, [field]: value };

      if (field === 'incotermId') {
        newData.timelineType =
          value === 'e0173dfd-e109-4054-8640-23d95befd326' ? 'ALD' : 'ADD';
        newData.incotermRouteId = '';
        newData.incotermLocationId = '';
        newData.incotermLocation2Id = '';
        newData.incotermLocation3Id = '';
        //newData.incotermFOB = [];
      }

      // if (field === 'incotermRouteId') {
      //   newData.incotermFOB = [];
      // }

      if (field === 'incotermPortType') {
        if (value === 'SinglePort') {
          newData.incotermLocation2Id = null;
          newData.incotermLocation3Id = null;
        }
        if (value === '2Port') {
          newData.incotermLocation3Id = null;
        }
      }

      return newData;
    });

    if (field === 'incotermMultiPortType') {
      const setter =
        deliveryincotermformData.id === null
          ? setQtyDeliveryDataTotalQty
          : setQtyDeliveryDataQuantity;

      setter(prevQty => {
        const updatedItems = prevQty.items.map(item => {
          const qty = item.quantity;

          if (value === '2Port') {
            const location1 = Math.floor(qty / 2);
            const location2 = qty - location1;
            return {
              ...item,
              quantityLocation1: location1,
              quantityLocation2: location2,
              quantityLocation3: 0,
            };
          }

          if (value === '3Port') {
            const location1 = Math.floor(qty / 3);
            const location2 = Math.floor(qty / 3);
            const location3 = qty - location1 - location2;
            return {
              ...item,
              quantityLocation1: location1,
              quantityLocation2: location2,
              quantityLocation3: location3,
            };
          }

          return item;
        });

        return { ...prevQty, items: updatedItems };
      });
    }
  };

  const handleDeliveryDateChangeIncoterm = e => {
    const { name, value } = e.target;
    setIncotermErrors(prev => ({ ...prev, [name]: null }));
    setDeliveryIncotermFormData(prev => ({
      ...prev,
      [name]: convertToDisplayDateFormat(value),
    }));
  };

  const handleDateChange =
    (fieldName, resetError = true) =>
    selectedDates => {
      setForm(prevForm => ({
        ...prevForm,
        [fieldName]: selectedDates[0] || '',
      }));

      if (resetError) {
        setErrors(prevErrors => ({
          ...prevErrors,
          [fieldName]: null,
        }));
      }
    };

  const handleDeliveryNumberChangeIncoterm = fieldName => e => {
    const input = e.target.value;
    if (isNumericOnly(input)) {
      setDeliveryIncotermFormData(prev => ({
        ...prev,
        [fieldName]: input === '' ? null : parseInt(input),
      }));
    }
  };

  const handleDeliveryTextChangeIncoterm = field => event => {
    setDeliveryIncotermFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const togglemodalquantityincoterm = () => {
    setModalQuantityIncoterm(!modalquantityincoterm);
  };

  const getRouteIdByIncoterm = (route = null, incotermId = null) => {
    if (incotermId === 'e0173dfd-e109-4054-8640-23d95befd327') {
      return 'e0173dfd-e109-4054-8640-23d95befd328';
    } else if (incotermId === 'e0173dfd-e109-4054-8640-23d95befd328') {
      return 'e0173dfd-e109-4054-8640-23d95befd329';
    } else if (incotermId === 'e0173dfd-e109-4054-8640-23d95befd329') {
      return 'e0173dfd-e109-4054-8640-23d95befd330';
    } else if (incotermId === 'e0173dfd-e109-4054-8640-23d95befd326') {
      return 'e0173dfd-e109-4054-8640-23d95befd326';
    } else if (route === 'e0173dfd-e109-4054-8640-23d95befd326') {
      return 'e0173dfd-e109-4054-8640-23d95befd326';
    } else if (route === 'e0173dfd-e109-4054-8640-23d95befd327') {
      return 'e0173dfd-e109-4054-8640-23d95befd327';
    } else {
      return incotermId; // default
    }
  };

  const fetchIncotermLocation = useCallback(
    async (route = null, routeid = null) => {
      // const strquery =
      //   id === null ? `IncotermRouteId=${routeid}` : `Description=${id}`;
      //const producttype = form.productNameId === "4588b5e6-7309-49d0-9107-59cbb96b6bd8" ? true : null;
      const isLpgCase = LPG_PRODUCT.includes(form.productNameId);
      const vars = getRouteIdByIncoterm(route, routeid);
      try {
        const response = await axios.get(
          `${api.API_URL}/incoterm/get-incoterm-location?IncotermRouteId=${vars}&PageNumber=1&PageSize=50&IsLpgCase=${isLpgCase}`,
          {
            withXSRFToken: true,
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const items = response.items.map(item => ({
          value: item.id,
          label: item.name,
        }));
        setIncoTermLocationData({ items });
      } catch (error) {
        logger.error('Error:', error);
      } finally {
        //setIsLoadingIncotermLoc(false);
      }
    },
    [form.productNameId, token]
  );

  const handleQuantityChange = (index, field, value) => {
    setIncotermErrors(prev => ({ ...prev, [`${field}_${index}`]: null }));
    // Tentukan setter berdasarkan kondisi
    const setter =
      deliveryincotermformData.id === null
        ? setQtyDeliveryDataTotalQty
        : setQtyDeliveryDataQuantity;

    // Gunakan setter dengan functional update
    setter(prev => {
      // Pastikan prev memiliki properti items (array)
      const updatedItems = prev.items.map((item, i) => {
        if (i === index) {
          const val = value ? parseInt(value, 10) : 0;
          const quantityTotal = item.quantity;

          if (field === 'quantityLocation1') {
            let location1 = val;
            if (location1 > quantityTotal) {
              Swal.fire(
                `Quantity Location1 cannot be more than ${quantityTotal}`
              );
              return item;
            }
            const remainder = quantityTotal - location1;

            if (deliveryincotermformData.incotermMultiPortType === '2Port') {
              const rest = { ...item };
              delete rest.quantityLocation3;
              return {
                ...rest,
                quantityLocation1: location1,
                quantityLocation2: remainder,
              };
            }

            const location2 = Math.floor(remainder / 2);
            const location3 = remainder - location2;
            return {
              ...item,
              quantityLocation1: location1,
              quantityLocation2: location2,
              quantityLocation3: location3,
            };
          }

          if (field === 'quantityLocation2') {
            let location2 = val;
            if (location2 > quantityTotal) {
              Swal.fire(`Total Quantity cannot be more than ${quantityTotal}`);
              return item;
            }
            const remainder = quantityTotal - location2;

            if (deliveryincotermformData.incotermMultiPortType === '2Port') {
              const rest = { ...item };
              delete rest.quantityLocation3;
              return {
                ...rest,
                quantityLocation1: remainder,
                quantityLocation2: location2,
              };
            }

            const location1 = Math.floor(remainder / 2);
            const location3 = remainder - location1;
            return {
              ...item,
              quantityLocation1: location1,
              quantityLocation2: location2,
              quantityLocation3: location3,
            };
          }

          if (field === 'quantityLocation3') {
            if (deliveryincotermformData.incotermMultiPortType === '2Port') {
              return item;
            }
            let location3 = val;
            if (location3 > quantityTotal) {
              Swal.fire(`Total Quantity cannot be more than ${quantityTotal}`);
              return item;
            }
            const remainder = quantityTotal - location3;
            const location1 = Math.floor(remainder / 2);
            const location2 = remainder - location1;
            return {
              ...item,
              quantityLocation1: location1,
              quantityLocation2: location2,
              quantityLocation3: location3,
            };
          }

          return item;
        }
        return item;
      });

      return { ...prev, items: updatedItems };
    });
  };

  // const [deliveryincotermformData, setDeliveryIncotermFormData] = useState({});
  const [deliveryincotermformData, setDeliveryIncotermFormData] = useState({
    quantityDeliveryTermId: null,
    incotermId: null,
    incotermPortType: null,
    incotermRouteId: null,
    incotermLocationId: null,
    incotermLocationName: '',
    incotermLocation2Name: '',
    incotermLocation2Id: null,
    incotermLocation3Name: '',
    incotermLocation3Id: null,
    incotermFOB: null,
    timelineType: null,
    startDate: '',
    endDate: '',
    timelineType2: null,
    startDate2: '',
    endDate2: '',
    timelineType3: null,
    startDate3: '',
    endDate3: '',
    alpha: null,
    loc1Draft1: '',
    loc1Draft2: '',
    loc1Loa1: '',
    loc1Loa2: '',
    loc1Dwt1: '',
    loc1Dwt2: '',
    loc1NotesForSTS: '',
    incotermMultiPortType: null,
    loc2Draft1: '',
    loc2Draft2: '',
    loc2Loa1: '',
    loc2Loa2: '',
    loc2Dwt1: '',
    loc2Dwt2: '',
    loc2NotesForSTS: '',
    loc3Draft1: '',
    loc3Draft2: '',
    loc3Loa1: '',
    loc3Loa2: '',
    loc3Dwt1: '',
    loc3Dwt2: '',
    loc3NotesForSTS: '',
    additionalInformation: '',
    quantityLocation1: '',
    quantityLocation2: '',
    quantityLocation3: '',
    id: null,
  });
  useEffect(() => {
    if (deliveryincotermformData.incotermRouteId) {
      fetchIncotermLocation(deliveryincotermformData.incotermRouteId, null);
    } else if (deliveryincotermformData.incotermId) {
      fetchIncotermLocation(null, deliveryincotermformData.incotermId);
    }
  }, [
    deliveryincotermformData.incotermId,
    deliveryincotermformData.incotermRouteId,
    fetchIncotermLocation,
  ]);

  const handleIncotermOpenModalNew = async deliveryid => {
    setDeliveryIncotermFormData({
      quantityDeliveryTermId: deliveryformData.id,
      incotermId: null,
      incotermPortType: null,
      incotermRouteId: null,
      incotermLocationId: null,
      incotermLocation2Id: null,
      incotermLocation3Id: null,
      incotermFOB: null,
      timelineType: null,
      startDate: null,
      endDate: null,
      timelineType2: null,
      startDate2: null,
      endDate2: null,
      timelineType3: null,
      startDate3: null,
      endDate3: null,

      alpha: null,
      loc1Draft1: null,
      loc1Draft2: null,
      loc1Loa1: null,
      loc1Loa2: null,
      loc1Dwt1: null,
      loc1Dwt2: null,
      loc1NotesForSTS: null,
      loc2Draft1: null,
      loc2Draft2: null,
      loc2Loa1: null,
      loc2Loa2: null,
      loc2Dwt1: null,
      loc2Dwt2: null,
      loc2NotesForSTS: null,

      loc3Draft1: null,
      loc3Draft2: null,
      loc3Loa1: null,
      loc3Loa2: null,
      loc3Dwt1: null,
      loc3Dwt2: null,
      loc3NotesForSTS: null,

      additionalInformation: null,
      id: null,
      quantity: null,
    });
    fetchDeliveryDataDetailTotalQty(deliveryid);
    togglemodalincoterm();
  };

  const selectedFieldsDeliveryIncotermFormData = {
    quantityDeliveryTermId: deliveryformData.id,
    incotermId: deliveryincotermformData?.incotermId ?? null,
    incotermPortType: deliveryincotermformData?.incotermPortType ?? null,
    incotermRouteId: deliveryincotermformData?.incotermRouteId ?? null,
    incotermLocationId: deliveryincotermformData?.incotermLocationId ?? null,
    incotermLocation2Id: deliveryincotermformData?.incotermLocation2Id ?? null,
    incotermLocation3Id: deliveryincotermformData?.incotermLocation3Id ?? null,

    incotermFOB: Array.isArray(deliveryincotermformData?.incotermFOB)
      ? deliveryincotermformData.incotermFOB
      : deliveryincotermformData?.incotermFOB
        ? [deliveryincotermformData.incotermFOB]
        : null,

    timelineType: deliveryincotermformData?.timelineType ?? null,
    startDate:
      convertToInputDateFormat(deliveryincotermformData?.startDate) ?? null,
    endDate:
      convertToInputDateFormat(deliveryincotermformData?.endDate) ?? null,
    timelineType2: deliveryincotermformData?.timelineType2 ?? null,
    startDate2:
      convertToInputDateFormat(deliveryincotermformData?.startDate2) ?? null,
    endDate2:
      convertToInputDateFormat(deliveryincotermformData?.endDate2) ?? null,

    timelineType3: deliveryincotermformData?.timelineType3 ?? null,
    startDate3:
      convertToInputDateFormat(deliveryincotermformData?.startDate3) ?? null,
    endDate3:
      convertToInputDateFormat(deliveryincotermformData?.endDate3) ?? null,

    alpha: deliveryincotermformData?.alpha ?? null,
    loc1Draft1: deliveryincotermformData?.loc1Draft1 ?? null,
    loc1Draft2: deliveryincotermformData?.loc1Draft2 ?? null,
    loc1Loa1: deliveryincotermformData?.loc1Loa1 ?? null,
    loc1Loa2: deliveryincotermformData?.loc1Loa2 ?? null,
    loc1Dwt1: deliveryincotermformData?.loc1Dwt1 ?? null,
    loc1Dwt2: deliveryincotermformData?.loc1Dwt2 ?? null,
    loc1NotesForSTS: deliveryincotermformData?.loc1NotesForSTS ?? null,
    loc2Draft1: deliveryincotermformData?.loc2Draft1 ?? null,
    loc2Draft2: deliveryincotermformData?.loc2Draft2 ?? null,
    loc2Loa1: deliveryincotermformData?.loc2Loa1 ?? null,
    loc2Loa2: deliveryincotermformData?.loc2Loa2 ?? null,
    loc2Dwt1: deliveryincotermformData?.loc2Dwt1 ?? null,
    loc2Dwt2: deliveryincotermformData?.loc2Dwt2 ?? null,
    loc2NotesForSTS: deliveryincotermformData?.loc2NotesForSTS ?? null,

    loc3Draft1: deliveryincotermformData?.loc3Draft1 ?? null,
    loc3Draft2: deliveryincotermformData?.loc3Draft2 ?? null,
    loc3Loa1: deliveryincotermformData?.loc3Loa1 ?? null,
    loc3Loa2: deliveryincotermformData?.loc3Loa2 ?? null,
    loc3Dwt1: deliveryincotermformData?.loc3Dwt1 ?? null,
    loc3Dwt2: deliveryincotermformData?.loc3Dwt2 ?? null,
    loc3NotesForSTS: deliveryincotermformData?.loc3NotesForSTS ?? null,
    multiportOption: deliveryincotermformData?.incotermMultiPortType ?? null,

    additionalInformation:
      deliveryincotermformData?.additionalInformation ?? null,

    quantity:
      deliveryincotermformData.id === null
        ? qtyDeliveryDataTotalQty.items
        : qtyDeliveryDataQuantity.items,
  };

  const parseIncotermDate = d => {
    if (!d) return null;
    const parts = d.split('/');
    if (parts.length !== 3) return null;
    const [day, month, year] = parts.map(Number);
    return new Date(year, month - 1, day);
  };

  const validateIncoterm = () => {
    const tempErrors = {};
    if (!deliveryincotermformData.incotermId) {
      tempErrors.incotermId = 'Please select Incoterm Type';
    }
    if (
      deliveryincotermformData.incotermId !==
        'e0173dfd-e109-4054-8640-23d95befd326' &&
      !deliveryincotermformData.incotermPortType
    ) {
      tempErrors.incotermPortType = 'Please select Port(s)';
    }
    if (
      deliveryincotermformData.incotermId ===
        'e0173dfd-e109-4054-8640-23d95befd326' &&
      !deliveryincotermformData.incotermRouteId
    ) {
      tempErrors.incotermRouteId = 'Please select Route';
    }

    const isALD =
      deliveryincotermformData.incotermId ===
      'e0173dfd-e109-4054-8640-23d95befd326';
    const isMultiPort =
      deliveryincotermformData.incotermPortType === 'MultiPort';
    const is2Port = deliveryincotermformData.incotermMultiPortType === '2Port';
    const is3Port = deliveryincotermformData.incotermMultiPortType === '3Port';

    const needsLocation1 = is2Port || is3Port;
    const needsLocation2 = is2Port || is3Port;
    const needsLocation3 = is3Port;

    const qtyItems =
      deliveryincotermformData.id === null
        ? qtyDeliveryDataTotalQty.items
        : qtyDeliveryDataQuantity.items;

    if (needsLocation1) {
      qtyItems.forEach((item, idx) => {
        if (!item.quantityLocation1 && item.quantityLocation1 !== 0) {
          tempErrors[`quantityLocation1_${idx}`] =
            `Row ${idx + 1}: Location 1 quantity is required`;
        }
      });
    }
    if (needsLocation2) {
      qtyItems.forEach((item, idx) => {
        if (!item.quantityLocation2 && item.quantityLocation2 !== 0) {
          tempErrors[`quantityLocation2_${idx}`] =
            `Row ${idx + 1}: Location 2 quantity is required`;
        }
      });
    }
    if (needsLocation3) {
      qtyItems.forEach((item, idx) => {
        if (!item.quantityLocation3 && item.quantityLocation3 !== 0) {
          tempErrors[`quantityLocation3_${idx}`] =
            `Row ${idx + 1}: Location 3 quantity is required`;
        }
      });
    }

    if (!isALD && deliveryincotermformData.incotermId) {
      if (!deliveryincotermformData.incotermLocationId) {
        tempErrors.incotermLocationId = 'Location is required';
      }
    }
    if (
      (is2Port || is3Port) &&
      !isALD &&
      isMultiPort &&
      !deliveryincotermformData.incotermLocation2Id
    ) {
      tempErrors.incotermLocation2Id = 'Location 2 is required';
    }
    if (isALD && isMultiPort) {
      const loc2 = deliveryincotermformData.incotermLocation2Id;
      const isEmpty = Array.isArray(loc2) ? loc2.length === 0 : !loc2;
      if (isEmpty) {
        tempErrors.incotermLocation2Id = 'Location 2 is required';
      }
    }

    if (
      is3Port &&
      !isALD &&
      isMultiPort &&
      !deliveryincotermformData.incotermLocation3Id
    ) {
      tempErrors.incotermLocation3Id = 'Location 3 is required';
    }

    if (!deliveryincotermformData.startDate) {
      tempErrors.startDate = 'Date From is required';
    }
    if (!deliveryincotermformData.endDate) {
      tempErrors.endDate = 'Date To is required';
    }
    if (needsLocation1) {
      if (!deliveryincotermformData.startDate2) {
        tempErrors.startDate2 = 'Date From is required';
      }
      if (!deliveryincotermformData.endDate2) {
        tempErrors.endDate2 = 'Date To is required';
      }
    }
    if (needsLocation3) {
      if (!deliveryincotermformData.startDate3) {
        tempErrors.startDate3 = 'Date From is required';
      }
      if (!deliveryincotermformData.endDate3) {
        tempErrors.endDate3 = 'Date To is required';
      }
    }

    const s1 = parseIncotermDate(deliveryincotermformData.startDate);
    const e1 = parseIncotermDate(deliveryincotermformData.endDate);
    if (s1 && e1 && s1 >= e1) {
      tempErrors.endDate = 'Date To must be after Date From';
    }
    const s2 = parseIncotermDate(deliveryincotermformData.startDate2);
    const e2 = parseIncotermDate(deliveryincotermformData.endDate2);
    if (s2 && e2 && s2 >= e2) {
      tempErrors.endDate2 = 'Date To must be after Date From';
    }
    const s3 = parseIncotermDate(deliveryincotermformData.startDate3);
    const e3 = parseIncotermDate(deliveryincotermformData.endDate3);
    if (s3 && e3 && s3 >= e3) {
      tempErrors.endDate3 = 'Date To must be after Date From';
    }

    if (Object.keys(tempErrors).length !== 0) {
      toast.error('Please Check Mandatory Fields..', {
        position: 'top-right',
        hideProgressBar: false,
        closeOnClick: false,
        className: 'bg-warning text-black',
      });
    }
    setIncotermErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const rejectIncotermLocationLimit = () => {
    toast.error('Location sudah melebihi batas Maksimum', {
      position: 'top-right',
      hideProgressBar: false,
      closeOnClick: false,
      className: 'bg-warning text-black',
    });
    return false;
  };

  // Batas 3 lokasi, dengan aturan berbeda per jenis incoterm.
  const validateIncotermLocationLimit = () => {
    // Form FOB: jumlah lokasi yang dipilih sendiri tidak dibatasi, tapi tetap
    // ditolak kalau lokasi non-FOB yang sudah tersimpan melebihi 3.
    if (deliveryincotermformData.incotermId === FOB_INCOTERM_ID) {
      if (incotermLocationTotals.location <= MAX_INCOTERM_LOCATIONS)
        return true;
      return rejectIncotermLocationLimit();
    }

    // Form non-FOB: hanya diperiksa saat lokasi FOB tersimpan lebih dari satu.
    if (incotermLocationTotals.fob <= 1) return true;

    const totalLocations =
      incotermLocationTotals.location +
      countFormIncotermLocations(deliveryincotermformData);

    if (totalLocations <= MAX_INCOTERM_LOCATIONS) return true;

    return rejectIncotermLocationLimit();
  };

  const handleAddDataDeliveryIncoterm = () => {
    if (!validateIncoterm()) return;
    if (!validateIncotermLocationLimit()) return;
    setModalIncoterm(!modalincoterm);
    handleAddedDataDeliveryIncoterm();
  };

  const handleAddedDataDeliveryIncoterm = async _e => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${api.API_URL}/quantity-incoterm`,
        selectedFieldsDeliveryIncotermFormData,
        {
          withXSRFToken: true,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchDeliveryDataDetail(response.id);
      toast.success(`Data has been Saved`);
      fetchDeliveryDataDetailIncoterm(deliveryformData.id);
      fetchDataDeliveryIncoterm(response.id);
      //quantityTableRef.current?.refresh(deliveryformData.id);
    } catch (error) {
      toast.success('Error handleAddedDataDeliveryIncoterm: ', error);

      setError(error.message);
    } finally {
      setIsLoading(false);
      handleSaveDraftProc(); // tetap jalan
      LoadDefault();
    }
  };

  const handleUpdDataDeliveryIncoterm = async _e => {
    try {
      setIsLoading(true);
      await axios.put(
        `${api.API_URL}/quantity-incoterm/${deliveryincotermformData.id}`,
        selectedFieldsDeliveryIncotermFormData,
        {
          withXSRFToken: true,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success(`Data Has been Updated`);
      fetchDeliveryDataDetailIncoterm(deliveryformData.id);
      fetchDataDeliveryIncoterm(deliveryincotermformData.id);
    } catch (error) {
      logger.error('Error handleUpdDataDeliveryIncoterm: ', error);

      setError(error.message);
    } finally {
      setIsLoading(false);
      handleSaveDraftProc();
      LoadDefault();
    }
  };

  const fetchDataDeliveryIncoterm = async Id => {
    try {
      const response = await axios.get(
        `${api.API_URL}/quantity-incoterm/${Id}`,
        {
          withXSRFToken: true,
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: `application/json`,
          },
        }
      );
      const data = response;
      if (!data) {
        logger.warn('Data tidak ditemukan dari API');
        return null;
      }

      setDeliveryIncotermFormData({
        quantityDeliveryTermId: data.quantityDeliveryTermId || null,
        incotermId: data.incoterm.id || null,
        incotermPortType: data.incotermPortType || null,
        incotermRouteId: data.incotermRoute?.id || null,

        incotermLocationId: data.incotermLocation?.id || null,
        incotermLocationName: data.incotermLocation?.name || null,
        incotermLocation2Name: data.incotermLocation2?.name || null,
        incotermLocation2Id: data.incotermLocation2?.id || null,
        incotermLocation3Name: data.incotermLocation3?.name || null,
        incotermLocation3Id: data.incotermLocation3?.id || null,

        incotermFOB: Array.isArray(data.incotermFOBLocations)
          ? data.incotermFOBLocations.map(loc => loc.id)
          : null,
        timelineType: data.timelineType || null,
        startDate: convertDisplayddMMyyyy(data.startDate) || null,
        endDate: convertDisplayddMMyyyy(data.endDate) || null,
        timelineType2: data.timelineType2 || null,
        startDate2: convertDisplayddMMyyyy(data.startDate2) || null,
        endDate2: convertDisplayddMMyyyy(data.endDate2) || null,
        timelineType3: data.timelineType3 || null,
        startDate3: convertDisplayddMMyyyy(data.startDate3) || null,
        endDate3: convertDisplayddMMyyyy(data.endDate3) || null,
        alpha: data.alpha || null,
        loc1Draft1: data.loc1Draft1 || null,
        loc1Draft2: data.loc1Draft2 || null,
        loc1Loa1: data.loc1Loa1 || null,
        loc1Loa2: data.loc1Loa2 || null,
        loc1Dwt1: data.loc1Dwt1 || null,
        loc1Dwt2: data.loc1Dwt2 || null,
        loc1NotesForSTS: data.loc1NotesForSTS || null,
        incotermMultiPortType: data.multiportOption || null,

        loc2Draft1: data.loc2Draft1 || null,
        loc2Draft2: data.loc2Draft2 || null,
        loc2Loa1: data.loc2Loa1 || null,
        loc2Loa2: data.loc2Loa2 || null,
        loc2Dwt1: data.loc2Dwt1 || null,
        loc2Dwt2: data.loc2Dwt2 || null,
        loc2NotesForSTS: data.loc2NotesForSTS || null,

        loc3Draft1: data.loc3Draft1 || null,
        loc3Draft2: data.loc3Draft2 || null,
        loc3Loa1: data.loc3Loa1 || null,
        loc3Loa2: data.loc3Loa2 || null,
        loc3Dwt1: data.loc3Dwt1 || null,
        loc3Dwt2: data.loc3Dwt2 || null,
        loc3NotesForSTS: data.loc3NotesForSTS || null,
        additionalInformation: data.additionalInformation || null,
        quantityLocation1:
          data.quantityInformationDetail?.[0]?.quantityLocation1 ?? null,
        quantityLocation2:
          data.quantityInformationDetail?.[0]?.quantityLocation2 ?? null,
        quantityLocation3:
          data.quantityInformationDetail?.[0]?.quantityLocation3 ?? null,
        id: data.id || null,
      });

      if (deliveryincotermformData.id === null) {
        const quantitiesArray = {
          items: data.quantityInformationDetail.map(item => ({
            quantityInformationId: item.quantityInformationId,
            quantity: item.qty,
            quantityLocation1: item.quantityLocation1 ?? '',
            quantityLocation2: item.quantityLocation2 ?? '',
            quantityLocation3: item.quantityLocation3 ?? '',
            id: item.quantityInformationId,
          })),
        };
        setQtyDeliveryDataTotalQty(quantitiesArray);
      } else {
        const quantityItems = data.quantityInformationDetail
          .map(item => ({
            quantityInformationId: item.quantityInformationId ?? null,
            id: item.id ?? null,
            quantity: item.qty ?? null,
            quantityLocation1: item.quantityLocation1 ?? null,
            quantityLocation2: item.quantityLocation2 ?? null,
            quantityLocation3: item.quantityLocation3 ?? null,
          }))
          .sort((a, b) => a.quantity - b.quantity);

        logger.log('hasil aman', quantityItems);
        setQtyDeliveryDataQuantity({ items: quantityItems });
      }
      return data;
    } catch (error) {
      logger.error('Error fetching data from Invoice API:', error);
      return null;
    }
  };

  const handleDelDataDeliveryIncotermDetail = async id => {
    try {
      setIsLoading(true);
      await axios.delete(`${api.API_URL}/quantity-incoterm/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success(`Data has been deleted`);
      fetchDeliveryDataDetailIncoterm(deliveryformData.id);
    } catch (error) {
      logger.error('Error handleDelDataDeliveryIncotermDetail: ', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
      LoadDefault();
    }
  };

  const handleIncotermOpenModal = async (
    Id = null,
    _IncotermId = null,
    _Section = null
  ) => {
    await fetchDataDeliveryIncoterm(Id);
    togglemodalincoterm();
  };

  const handleViewOrigin = (locations, routes = 'Origin') => {
    setViewOriginLocations(locations || []);
    setViewOriginRoutes(routes);
    setViewOriginModal(true);
  };

  const handleClauseCheckBoxChange = e => {
    const { name, checked } = e.target;
    const itemId = name.replace('isShowToDMUT_', '');
    const originalItem =
      dataCause?.items?.find(i => String(i.id) === String(itemId)) || {};
    const val = checked ? 1 : 0;
    setDataCause(dataCause => {
      const updatedItem = {
        ...originalItem,
        id: String(itemId),
        clauseDetailId: originalItem.clauseDetailId ?? null,
        clauseDescription:
          dataCause.procurementClauseDetails?.find(
            d => String(d.id) === String(itemId)
          )?.clauseDescription ??
          originalItem.clauseDescription ??
          '',
        isShowToDMUT: val,
      };
      const prev = dataCause.procurementClauseDetails ?? dataCause?.items ?? [];
      const idx = prev.findIndex(i => String(i.id) === String(itemId));
      const procurementClauseDetails =
        idx >= 0
          ? prev.map((i, n) => (n === idx ? { ...i, ...updatedItem } : i))
          : [...prev, updatedItem];
      const rest = { ...dataCause };
      delete rest[itemId];
      delete rest[name];
      return {
        ...rest,
        procurementClauseDetails,
      };
    });
  };

  const fetchDeliveryDataDetailTotalQty = async id => {
    try {
      const response = await fetch(
        `${api.API_URL}/quantity-information?QuantityDeliveryTermId=${id}&SortBy=Quantity&IsDescending=false`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      const items = result.data.items.map(item => ({
        quantityDeliveryTermId: item.quantityDeliveryTermId,
        quantityInformationId: item.id,
        quantity: item.quantity,
        quantityIncotermId: item.quantityIncotermId,
        ids: item.id,
        quantityLocation1: '',
        quantityLocation2: '',
        quantityLocation3: '',
        id: item.quantityDeliveryTermId || crypto.randomUUID(),
        // Math.random().toString(36).substr(2, 9), // fallback ID
      }));
      setQtyDeliveryDataTotalQty({ items });
    } catch (error) {
      // Handle error silently or add logging as needed
      logger.error('Error fetchDeliveryDataDetailTotalQty: ', error);
    }
  };

  const HighlightColor = () => {
    const handleChange = e => {
      const value = e.target.value;
      const selection = window.getSelection();

      if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
        e.target.value = '';
        return;
      }

      const range = selection.getRangeAt(0);

      if (value === 'none') {
        let node = range.commonAncestorContainer;
        if (node.nodeType === 3) node = node.parentNode;
        const existingSpan = node.closest?.('span[data-highlight]');
        if (existingSpan) {
          const parent = existingSpan.parentNode;
          while (existingSpan.firstChild) {
            parent.insertBefore(existingSpan.firstChild, existingSpan);
          }
          parent.removeChild(existingSpan);
        }
      } else {
        const commonAncestor =
          range.commonAncestorContainer.nodeType === 1
            ? range.commonAncestorContainer
            : range.commonAncestorContainer.parentNode;
        const existingSpan = commonAncestor.closest?.('span[data-highlight]');
        if (existingSpan) {
          const parent = existingSpan.parentNode;
          while (existingSpan.firstChild) {
            parent.insertBefore(existingSpan.firstChild, existingSpan);
          }
          parent.removeChild(existingSpan);
        }

        const span = document.createElement('span');
        span.setAttribute('data-highlight', 'true');
        span.style.backgroundColor = value;
        range.surroundContents(span);
      }

      e.target.value = '';
    };

    return (
      <select
        className='form-select form-select-sm'
        style={{ width: '120px' }}
        onChange={handleChange}
        defaultValue=''
      >
        <option value=''>Highlight</option>
        <option value='none'>None</option>
        <option value='#ffff00'>Yellow</option>
        <option value='#90EE90'>Green</option>
        <option value='#87CEFA'>Blue</option>
        <option value='#FFC0CB'>Pink</option>
        <option value='#FFA500'>Orange</option>
      </select>
    );
  };

  const [activeSection, setActiveSection] = useState('req-sec-contract');
  const [expandedClauses, setExpandedClauses] = useState({});

  const fetchTemplateByClauseDetailId = async clauseDetailId => {
    if (templatesByClauseDetailId[clauseDetailId]) return;
    try {
      const response = await axios.get(
        `${api.API_URL}/template-procurement-document?PageNumber=1&PageSize=50&ClauseDetailId=${clauseDetailId}`,
        {
          withXSRFToken: true,
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setTemplatesByClauseDetailId(prev => ({
        ...prev,
        [clauseDetailId]: response.items || [],
      }));
    } catch (error) {
      logger.error('Error fetchTemplateByClauseDetailId:', error);
    }
  };

  const toggleClauseCard = id => {
    setExpandedClauses(prev => {
      const next = !prev[id];
      if (next) {
        const clauseItem = dataCause.items?.find(item => item.id === id);
        if (
          clauseItem?.clauseDetailId &&
          !templatesByClauseDetailId[clauseItem.clauseDetailId]
        ) {
          fetchTemplateByClauseDetailId(clauseItem.clauseDetailId);
        }
      }
      return { ...prev, [id]: next };
    });
  };

  useEffect(() => {
    if (isLoading) return undefined;
    const observer = new IntersectionObserver(
      entries => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveSection(visible[0].target.id);
        }
      },
      { rootMargin: '-120px 0px -55% 0px' }
    );
    REQ_SECTIONS.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [isLoading]);

  const scrollToSection = id => {
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    if (form.productTypeId) {
      CompositionVar(form.productTypeId);
    }
  }, [form.productTypeId]);

  const CompositionVar = action => {
    //let comptitle = "";
    switch (action) {
      case '4588b5e6-7309-49d0-9107-59cbb96b6b11':
        setCompTile('Refrigerated Propane');
        setSecTile('Pressurized Butane');
        setButaneSection(true);
        setPropaneSection(false);
        break;
      case '4588b5e6-7309-49d0-9107-59cbb96b6b12':
        setCompTile('Refrigerated Butane');
        setSecTile('Pressurized Butane');
        setButaneSection(true);
        setPropaneSection(false);
        break;

      case '4588b5e6-7309-49d0-9107-59cbb96b6b13':
        setCompTile('LPG Mixed Pressurized');
        setSecTile('Pressurized Butane');
        setButaneSection(true);
        setPropaneSection(false);
        break;

      case '4588b5e6-7309-49d0-9107-59cbb96b6b19':
        setCompTile('Pressurized Propane');
        setSecTile('Pressurized Butane');
        setButaneSection(false);
        setPropaneSection(true);
        break;
      case '4588b5e6-7309-49d0-9107-59cbb96b6b18':
        setCompTile('Refrigrated Propane');
        setSecTile('Refrigrated Butane');
        setButaneSection(false);
        setPropaneSection(true);
        break;
      case '4588b5e6-7309-49d0-9107-59cbb96b6b15':
        setCompTile('Pressurized Butane ');
        setSecTile('Pressurized Butane');
        setButaneSection(true);
        setPropaneSection(false);
        break;
      case '4588b5e6-7309-49d0-9107-59cbb96b6b14':
        setCompTile('Pressurized Propane');
        setSecTile('Pressurized Butane');
        setButaneSection(true);
        setPropaneSection(false);

        break;
    }
  };

  return (
    <React.Fragment>
      <Row>
        <div>
          <Row>
            <Col sm={12}>
              <div color='dark' className='d-flex align-items-center mb-2  '>
                <div className='flex-grow-1'>
                  <h5 className='mb-1'>
                    <i className='ri-file-list-3-line me-2'></i>
                    Requirement Information
                  </h5>

                  <p className='text-muted mb-2 fs-12 '>
                    Complete all requirement sections below, then save as draft
                    or save &amp; complete.
                  </p>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        <div className='d-flex align-items-start gap-3'>
          <div className='req-nav d-none d-lg-block'>
            <div className='req-nav-title'>Sections</div>
            {REQ_SECTIONS.map(s => (
              <button
                key={s.id}
                type='button'
                className={classnames('req-nav-link', {
                  active: activeSection === s.id,
                })}
                onClick={() => {
                  setActiveSection(s.id);
                  scrollToSection(s.id);
                }}
              >
                <i className={`${s.icon} me-2`}></i>
                {s.label}
              </button>
            ))}
          </div>
          <div className='flex-grow-1' style={{ minWidth: 0 }}>
            <Card className='border shadow-sm'>
              <CardBody>
                {isLoading ? (
                  <Col lg={12} className='text-center'>
                    <div className='text-center'>
                      <div
                        className='spinner-border spinner-border-sm'
                        role='status'
                      >
                        <span className='visually-hidden'>Loading...</span>
                      </div>
                    </div>
                  </Col>
                ) : error ? (
                  <Col lg={12} className='text-center'>
                    <div className='text-center'>
                      <div className='alert alert-danger'>{error}</div>
                    </div>
                  </Col>
                ) : (
                  <>
                    <Row>
                      <Col lg={12}>
                        {/* <div className="d-flex align-items-center border-top border-1 border-dark "></div> */}
                        <div
                          style={{
                            borderTopColor: '#b0b8b2',
                            borderTopWidth: '1px',
                            borderTopStyle: 'solid',
                          }}
                        ></div>
                        <div>
                          <p
                            id='req-sec-contract'
                            className=' mt-2 form-label mb-1 fs-13 text-uppercase fw-semibold'
                          >
                            Contract
                          </p>

                          <p className='text-muted mb-2 fs-12 '>
                            Choose the contract type and set its period.
                          </p>
                        </div>
                      </Col>

                      {form.isProcurementCompleted === false ? (
                        <>
                          <Col lg={2}>
                            <Label className='form-label'>
                              Contract Type :
                            </Label>
                            <div className='form-check card-radio'>
                              <Input
                                id='contractType01'
                                name='contractType'
                                type='radio'
                                value='1'
                                className='form-check-input'
                                onChange={e => {
                                  handleTypeChange(e);
                                  setErrors(prev => ({
                                    ...prev,
                                    contractType: null,
                                  }));
                                }}
                                checked={parseInt(form.contractType, 10) === 1}
                              />

                              <Label
                                className='form-check-label'
                                for='contractType01'
                              >
                                <span className='fs-16  me-2'>
                                  <i className=' ri-file-list-2-line align-bottom'></i>
                                </span>
                                <span className='fs-15 mb-1 text-wrap '>
                                  TERM
                                </span>
                              </Label>
                            </div>
                            {errors.contractType && (
                              <div className='text-danger d-flex align-items-center gap-1'>
                                <i className='mdi mdi-alert-circle-outline'></i>
                                <span>{errors.contractType}</span>
                              </div>
                            )}
                          </Col>
                          <Col lg={2}>
                            <Label className='form-label'></Label>
                            <div className='form-check card-radio'>
                              <Input
                                id='contractType02'
                                name='contractType'
                                type='radio'
                                value='2'
                                className='form-check-input'
                                onChange={e => {
                                  handleTypeChange(e);
                                  setErrors(prev => ({
                                    ...prev,
                                    contractType: null,
                                    contractPeriodTermEnd: null,
                                  }));
                                }}
                                checked={parseInt(form.contractType, 10) === 2}
                              />
                              <Label
                                className='form-check-label'
                                for='contractType02'
                              >
                                <span className='fs-16  me-2'>
                                  <i className=' ri-file-list-2-line align-bottom'></i>
                                </span>
                                <span className='fs-15 mb-1 text-wrap '>
                                  SPOT
                                </span>
                              </Label>
                            </div>
                          </Col>
                        </>
                      ) : (
                        <Col lg={4}>
                          <Label for='contractType' className='form-label'>
                            Contract Type :
                          </Label>

                          <Input
                            type='text'
                            className='form-control bg-light border-1 text-center  fw-semibold fs-14'
                            id='contractType'
                            name='contractType'
                            readOnly
                            disabled={true}
                            value={
                              parseInt(form?.contractType ?? 0, 10) === 1
                                ? 'TERM'
                                : 'SPOT'
                            }
                          />
                        </Col>
                      )}

                      <Col lg={2}>
                        <div className='mb-2'>
                          <Label
                            for='contractPeriodTermStart'
                            className='form-label'
                          >
                            {!isendDate ? 'Start Period' : 'Period'}{' '}
                          </Label>
                          {form.isProcurementCompleted === true ? (
                            <Flatpickr
                              key='contractPeriodTermStartreadonly'
                              value={
                                form.contractPeriodTermStart
                                  ? new Date(form.contractPeriodTermStart)
                                  : null
                              }
                              options={{
                                altInput: true, // tampilkan format terpisah
                                altFormat: 'M Y', // format tampilan: Mar 2025
                                dateFormat: 'Y-m', // format simpan: 2025-03
                                locale: Indonesian,
                                clickOpens: !isendDate,
                                allowInput: !isendDate,
                              }}
                              //dateFormat='MMMM yyyy'
                              className='form-control bg-light border-1'
                              id='contractPeriodTermEndRo'
                              name='contractPeriodTermEndRo'
                              locale='id'
                              readOnly
                              disabled={true}
                            />
                          ) : (
                            <Flatpickr
                              key='contractPeriodTermStarteditable'
                              value={
                                form.contractPeriodTermStart
                                  ? new Date(form.contractPeriodTermStart)
                                  : null
                              }
                              options={{
                                altInput: true,
                                altFormat: 'M Y',
                                dateFormat: 'Y-m',
                                locale: Indonesian,
                                plugins: [
                                  new monthSelectPlugin({
                                    shorthand: false,
                                    dateFormat: 'Y-m',
                                    altFormat: 'F Y',
                                    theme: 'light',
                                  }),
                                ],
                              }}
                              onChange={([date]) => {
                                if (date instanceof Date && !isNaN(date)) {
                                  const endOfMonth = new Date(
                                    date.getFullYear(),
                                    date.getMonth() + 1,
                                    0
                                  );
                                  setForm(form => ({
                                    ...form,
                                    contractPeriodTermStart: endOfMonth,
                                  }));
                                  setErrors(prev => ({
                                    ...prev,
                                    contractPeriodTermStart: null,
                                  }));
                                } else {
                                  setForm(form => ({
                                    ...form,
                                    contractPeriodTermStart: null,
                                  }));
                                }
                              }}
                              className='form-control'
                              placeholder='Pilih Bulan'
                              id='contractPeriodTermStart'
                              name='contractPeriodTermStart'
                              locale='id'
                            />
                          )}
                          {errors.contractPeriodTermStart && (
                            <div className='text-danger d-flex align-items-center gap-1'>
                              <i className='mdi mdi-alert-circle-outline'></i>
                              <span>{errors.contractPeriodTermStart}</span>
                            </div>
                          )}{' '}
                        </div>
                      </Col>

                      <Col lg={2}>
                        {!isendDate && (
                          <div className='mb-2'>
                            <Label
                              for='contractPeriodTermEnd'
                              className='form-label'
                            >
                              {' '}
                              End Period{' '}
                            </Label>
                            {form.isProcurementCompleted === true ? (
                              <Flatpickr
                                key='contractPeriodTermEndreadonly'
                                value={
                                  form.contractPeriodTermEnd
                                    ? new Date(form.contractPeriodTermEnd)
                                    : null
                                }
                                options={{
                                  altInput: true, // tampilkan format terpisah
                                  altFormat: 'M Y', // format tampilan: Mar 2025
                                  dateFormat: 'Y-m', // format simpan: 2025-03
                                  locale: Indonesian,
                                  clickOpens: !isendDate,
                                  allowInput: !isendDate,
                                }}
                                //dateFormat='MMMM yyyy'
                                className='form-control bg-light border-1'
                                id='contractPeriodTermEnd'
                                name='contractPeriodTermEnd'
                                locale='id'
                                disabled={true}
                              />
                            ) : (
                              <Flatpickr
                                key='contractPeriodTermEndeditable'
                                value={
                                  form.contractPeriodTermEnd
                                    ? new Date(form.contractPeriodTermEnd)
                                    : null
                                }
                                options={{
                                  altInput: true, // tampilkan format terpisah
                                  altFormat: 'M Y', // format tampilan: Mar 2025
                                  dateFormat: 'Y-m', // format simpan: 2025-03
                                  locale: Indonesian,
                                  clickOpens: !isendDate,
                                  allowInput: !isendDate,
                                  plugins: [
                                    new monthSelectPlugin({
                                      shorthand: false,
                                      dateFormat: 'Y-m', // Format yang disimpan (misalnya: 2025-02)
                                      altFormat: 'F Y', // Format yang ditampilkan (misalnya: Februari 2025)
                                      theme: 'light',
                                    }),
                                  ],
                                }}
                                onChange={([date]) => {
                                  if (isendDate) return;
                                  if (date instanceof Date && !isNaN(date)) {
                                    const endOfMonth = new Date(
                                      date.getFullYear(),
                                      date.getMonth() + 1,
                                      0
                                    );
                                    setForm(form => ({
                                      ...form,
                                      contractPeriodTermEnd: endOfMonth,
                                    }));
                                    setErrors(prev => ({
                                      ...prev,
                                      contractPeriodTermEnd: null,
                                    }));
                                  } else {
                                    setForm(form => ({
                                      ...form,
                                      contractPeriodTermEnd: null,
                                    }));
                                  }
                                }}
                                className='form-control'
                                placeholder='Pilih Bulan'
                                id='contractPeriodTermEnd'
                                name='contractPeriodTermEnd'
                                locale='id'
                                //readOnly
                                disabled={isendDate}
                              />
                            )}
                            {errors.contractPeriodTermEnd && (
                              <div className='text-danger d-flex align-items-center gap-1'>
                                <i className='mdi mdi-alert-circle-outline'></i>
                                <span>{errors.contractPeriodTermEnd}</span>
                              </div>
                            )}{' '}
                          </div>
                        )}
                      </Col>
                    </Row>
                  </>
                )}

                <Row>
                  <Col sm={12}>
                    {/* <div className="d-flex align-items-center border-top border-1 border-dark "></div> */}
                    <div
                      style={{
                        borderTopColor: '#b0b8b2',
                        borderTopWidth: '1px',
                        borderTopStyle: 'solid',
                      }}
                    ></div>

                    <p
                      id='req-sec-product'
                      className=' mt-2 form-label mb-1 fs-13 text-uppercase fw-semibold'
                    >
                      Product Specification / Quantity
                    </p>
                    <p className='text-muted mb-2 fs-12 '>
                      Select the product, its type, tolerance, and detailed
                      specification.
                    </p>
                  </Col>

                  <Col sm={4}>
                    <div className='mb-2'>
                      <p className='form-label '>Product Name </p>
                      <Select
                        name='productNameId'
                        id='productNameId'
                        options={productName.items}
                        isClearable
                        placeholder='Select Product Name'
                        value={
                          productName.items?.find(
                            obj => obj.value === form.productNameId
                          ) || null
                        }
                        onChange={e => {
                          handleProductNameChange(e);
                          setErrors(prev => ({
                            ...prev,
                            productNameId: null,
                          }));
                        }}
                        isDisabled={
                          form.isProcurementCompleted ||
                          qtyDeliveryData.items.length > 0
                        }
                        styles={{
                          singleValue: base => ({
                            ...base,
                            color: '#000',
                          }),
                          input: base => ({
                            ...base,
                            color: '#000',
                          }),
                        }}
                      />
                      {errors.productNameId && (
                        <div className='text-danger d-flex align-items-center gap-1'>
                          <i className='mdi mdi-alert-circle-outline'></i>
                          <span>{errors.productNameId}</span>
                        </div>
                      )}{' '}
                    </div>
                  </Col>

                  <Col sm={3}>
                    <div className='mb-2'>
                      <p className='form-label '>Product Type </p>
                      <Select
                        name='productTypeId'
                        id='productTypeId'
                        options={productTypeData.items}
                        placeholder={
                          !form.productNameId
                            ? 'Please Select Product Name First'
                            : 'Please Select'
                        }
                        value={
                          productTypeData.items?.find(
                            obj => obj.value === form.productTypeId
                          ) || null
                        }
                        onChange={e => {
                          handleProductTypeChange(e);
                          setErrors(prev => ({
                            ...prev,
                            productTypeId: null,
                          }));
                        }}
                        isDisabled={
                          form.isProcurementCompleted ||
                          qtyDeliveryData.items.length > 0
                        }
                        styles={{
                          singleValue: base => ({
                            ...base,
                            color: '#000',
                          }),
                          input: base => ({
                            ...base,
                            color: '#000',
                          }),
                        }}
                      />
                      {errors.productTypeId && (
                        <div className='text-danger d-flex align-items-center gap-1'>
                          <i className='mdi mdi-alert-circle-outline'></i>
                          <span>{errors.productTypeId}</span>
                        </div>
                      )}{' '}
                    </div>
                  </Col>

                  <Col sm={2}>
                    <div className='mb-2'>
                      <p className='form-label '>Operational Tolerance</p>
                      <div className='input-group'>
                        <Input
                          type='number'
                          id='operationalTolerance'
                          name='operationalTolerance'
                          className={
                            form.isProcurementCompleted === false
                              ? 'form-control'
                              : 'form-control bg-light border-1'
                          }
                          value={form.operationalTolerance || ''}
                          onChange={handleNumberChange('operationalTolerance')}
                          readOnly={form.isProcurementCompleted !== false}
                        />

                        <span className='input-group-text' id='basic-addon2'>
                          %
                        </span>
                      </div>
                    </div>
                  </Col>

                  <Col sm={3}>
                    <div className='mt-4'>
                      <Select
                        id='partyType'
                        name='partyType'
                        options={PartyType}
                        placeholder='Please Select'
                        isClearable
                        value={
                          PartyType[0].options.find(
                            option => option.value === form.partyType
                          ) || null
                        }
                        onChange={e => {
                          handleSelectChange(e, 'partyType');
                        }}
                      />
                    </div>
                  </Col>
                </Row>

                {LPG_PRODUCT.includes(form.productNameId) && (
                  <>
                    <Row>
                      <Col sm={12}>
                        {/* <div className="d-flex align-items-center border-top border-1 border-dark "></div> */}
                        <div
                          style={{
                            borderTopColor: '#b0b8b2',
                            borderTopWidth: '1px',
                            borderTopStyle: 'solid',
                          }}
                        ></div>

                        <p
                          id='req-sec-composition'
                          className=' mt-2 form-label mb-1 fs-13 text-uppercase fw-semibold'
                        >
                          Composition
                        </p>
                        <p className='text-muted mb-2 fs-12 '>
                          Define the composition breakdown for each component.
                        </p>
                      </Col>
                    </Row>
                    {butanesection && (
                      <>
                        <Row>
                          <Col sm={4}>
                            <div className='mb-2'>
                              <p className='form-label '>Cargo Type </p>
                              <Select
                                id='cargoType'
                                name='cargoType'
                                options={CargoType}
                                placeholder='Please Select'
                                isClearable
                                value={
                                  CargoType[0].options.find(
                                    option => option.value === form.cargoType
                                  ) || null
                                }
                                onChange={e => {
                                  handleSelectChange(e, 'cargoType');
                                }}
                              />
                            </div>
                          </Col>

                          <Col sm={4}>
                            <Composition
                              form={form}
                              setForm={setForm}
                              comptitle={comptitle}
                            />
                          </Col>
                        </Row>
                      </>
                    )}

                    {propanesection && (
                      <>
                        <Row>
                          <Col sm={12}>
                            <CargoTypeComposition
                              form={form}
                              setForm={setForm}
                              comptitle={comptitle}
                              sectitle={sectitle}
                              qtyDeliveryData={qtyDeliveryData}
                            />
                          </Col>
                        </Row>
                      </>
                    )}
                  </>
                )}

                <Row>
                  <Col sm={12}>
                    <div
                      style={{
                        borderTopColor: '#b0b8b2',
                        borderTopWidth: '1px',
                        borderTopStyle: 'solid',
                      }}
                    ></div>

                    <Card>
                      <p
                        id='req-sec-product'
                        className=' mt-2 form-label mb-1 fs-13  fw-semibold'
                      >
                        Product Specification
                      </p>
                      {errors.productSpecification && (
                        <div className='text-danger d-flex align-items-center gap-1'>
                          <i className='mdi mdi-alert-circle-outline'></i>
                          <span>{errors.productSpecification}</span>
                        </div>
                      )}{' '}
                      <EditorProvider>
                        <Editor
                          id='productSpecification'
                          name='productSpecification'
                          value={form.productSpecification || ''}
                          onChange={e => {
                            handleNoteTextChange(e, 'productSpecification');
                            setErrors(prev => ({
                              ...prev,
                              productSpecification: '',
                            }));
                          }}
                          style={{ height: '200px', overflowY: 'auto' }}
                          className={
                            form.isProcurementCompleted === false
                              ? 'form-control'
                              : 'form-control bg-light border-0'
                          }
                        >
                          {form.isProcurementCompleted === false && (
                            <Toolbar>
                              <BtnBold />
                              <BtnItalic />
                              <BtnBulletList />
                              <BtnClearFormatting />
                              <BtnLink />
                              <BtnNumberedList />
                              <BtnRedo />
                              <BtnStrikeThrough />
                              <BtnStyles />
                              <BtnUnderline />
                              <BtnUndo />

                              <Separator />
                            </Toolbar>
                          )}
                        </Editor>
                      </EditorProvider>
                    </Card>
                  </Col>
                </Row>

                <Row>
                  <Col sm={12}>
                    <div
                      className='d-flex align-items-center '
                      style={{
                        borderTopColor: '#b0b8b2',
                        borderTopWidth: '1px',
                        borderTopStyle: 'solid',
                      }}
                    >
                      <div className='flex-grow-1'>
                        <p
                          id='req-sec-delivery'
                          className=' mt-2 form-label mb-1 fs-13 text-uppercase fw-semibold'
                        >
                          Quantity & Delivery Time
                        </p>

                        <p className='text-muted mb-2 fs-12 '>
                          Manage delivery quantities, schedules, and
                          destinations.
                        </p>
                      </div>
                      <div className='flex-shrink-0'>
                        {form.isProcurementCompleted === false && (
                          <Button
                            color='success'
                            className='btn-label btn btn-sm'
                            onClick={() => handleDeliveryOpenModalNew()}
                          >
                            {' '}
                            <i className='ri-check-double-line label-icon align-middle fs-16 '></i>{' '}
                            Add Other Optional Delivery{' '}
                          </Button>
                        )}
                      </div>
                    </div>
                  </Col>

                  <Col xl={12}>
                    <div className='mb-1'>
                      {/* <CardBody> */}

                      {isLoading ? (
                        <Col lg={12} className='text-center'>
                          <div className='text-center'>
                            <div
                              className='spinner-border spinner-border-sm'
                              role='status'
                            >
                              <span className='visually-hidden'>
                                Loading...
                              </span>
                            </div>
                          </div>
                        </Col>
                      ) : error ? (
                        <Col lg={12} className='text-center'>
                          <div className='text-center'>
                            <div className='alert alert-danger'>{error}</div>
                          </div>
                        </Col>
                      ) : (
                        <>
                          {qtyDeliveryData.items.length > 0 ? (
                            qtyDeliveryData.items.map((data, index) => (
                              <Col sm={12} key={`${data.id}-${index}`}>
                                {/* <ActionMenu /> */}
                                <Col sm={12}>
                                  <div
                                    style={{
                                      borderTopColor: '#b0b8b2',
                                      borderTopWidth: '1px',
                                      borderTopStyle: 'solid',
                                    }}
                                  >
                                    <Row>
                                      <Col sm={12}>
                                        <UncontrolledDropdown className='float-end mt-2'>
                                          <DropdownToggle
                                            tag='a'
                                            className='btn btn-soft-secondary btn-sm'
                                          >
                                            <i className='ri-more-fill align-middle'></i>
                                          </DropdownToggle>
                                          <DropdownMenu className='dropdown-menu-end'>
                                            {!form.isProcurementCompleted && (
                                              <li>
                                                <DropdownItem
                                                  onClick={() =>
                                                    handleDeleteQtyDelivery(
                                                      data.id
                                                    )
                                                  }
                                                >
                                                  <i className=' ri-delete-bin-2-line align-bottom me-2 text-muted'></i>{' '}
                                                  Delete
                                                </DropdownItem>
                                              </li>
                                            )}

                                            <li>
                                              <DropdownItem
                                                className='edit-item-btn'
                                                data-bs-toggle='modal'
                                                onClick={() =>
                                                  handleDeliveryOpenModal(
                                                    data.id
                                                  )
                                                }
                                              >
                                                <i className='ri-align-justify align-bottom me-2 text-muted'></i>{' '}
                                                Detail
                                              </DropdownItem>
                                            </li>
                                          </DropdownMenu>
                                        </UncontrolledDropdown>
                                      </Col>
                                    </Row>
                                  </div>
                                </Col>
                                <Col xxl={12}>
                                  <Nav tabs className='nav nav-tabs  mb-3'>
                                    <NavItem>
                                      <NavLink
                                        style={{ cursor: 'pointer' }}
                                        className={classnames({
                                          active:
                                            activeTabs[data.id] ===
                                            `${data.id}1`,
                                        })}
                                        onClick={() =>
                                          toggle(data.id, `${data.id}1`)
                                        }
                                      >
                                        <label className='text-uppercase'>
                                          {' '}
                                          Quantity Delivery Time
                                        </label>
                                      </NavLink>
                                    </NavItem>

                                    <NavItem>
                                      <NavLink
                                        style={{ cursor: 'pointer' }}
                                        className={classnames({
                                          active:
                                            activeTabs[data.id] ===
                                            `${data.id}2`,
                                        })}
                                        onClick={() =>
                                          toggle(data.id, `${data.id}2`)
                                        }
                                      >
                                        <label className='text-uppercase'>
                                          {' '}
                                          Pricing
                                        </label>
                                      </NavLink>
                                    </NavItem>
                                  </Nav>

                                  <TabContent activeTab={activeTabs[data.id]}>
                                    <TabPane tabId={`${data.id}1`} id='home'>
                                      <Row>
                                        <Col sm={3}>
                                          <Row>
                                            <Col lg={12}>
                                              <div className=' mt-2  field-border-brown'>
                                                <Label
                                                  for='deliveryUnitreadonly'
                                                  className='text-muted'
                                                >
                                                  Delivery Unit :
                                                </Label>
                                                <p className='form-label fw-semibold fs-12 '>
                                                  {data.deliveryUnit || '-'}
                                                </p>
                                              </div>
                                            </Col>
                                            <Col lg={12}>
                                              <div className=' mt-2  field-border-brown'>
                                                <Label
                                                  for='frequencyreadonly'
                                                  className='text-muted'
                                                >
                                                  Frequency :
                                                </Label>
                                                <p className='form-label fw-semibold fs-12 '>
                                                  {data.frequency || '-'}
                                                </p>
                                              </div>
                                            </Col>

                                            {/* <Col lg={6}>
                                              <div className='mb-2'>
                                                <Label
                                                  for='frequencyreadonly'
                                                  className='form-label '
                                                >
                                                  Frequency
                                                </Label>
                                                <Input
                                                  type='text'
                                                  className='form-control bg-light border-1'
                                                  id='frequencyreadonly'
                                                  name='frequencyreadonly'
                                                  readOnly
                                                  value={data.frequency || ''}
                                                />
                                              </div>
                                            </Col> */}
                                          </Row>
                                        </Col>

                                        <QuantityTableFragment
                                          ref={quantityRefs.current[index]}
                                          incoTermTable={incoTermTable}
                                          dataid={data.id}
                                          token={token}
                                          unitSizeType={unitSizeType}
                                        />

                                        <IncotermTableFragment
                                          incoTermTable={incoTermTable}
                                          dataid={data.id}
                                          token={token}
                                          onViewOrigin={handleViewOrigin}
                                        />
                                      </Row>
                                    </TabPane>

                                    <TabPane tabId={`${data.id}2`} id='product'>
                                      <Col sm={12}>
                                        <Row>
                                          <Col sm={5}>
                                            <Row>
                                              <Col sm={6}>
                                                <div className=' mt-2  field-border-brown'>
                                                  <Label className='text-muted'>
                                                    Delivery Unit :
                                                  </Label>
                                                  <p className='form-label fw-semibold fs-12 '>
                                                    {data.deliveryUnit || '-'}
                                                  </p>
                                                </div>
                                              </Col>

                                              <Col sm={6}>
                                                <div className=' mt-2  field-border-brown'>
                                                  <Label className='text-muted'>
                                                    Pricing Period :
                                                  </Label>
                                                  <p className='form-label fw-semibold fs-12 '>
                                                    {PricingPeriodData[0].options.find(
                                                      option =>
                                                        option.value ===
                                                        data.pricingPeriod
                                                    )?.label || '-'}
                                                  </p>
                                                </div>
                                              </Col>

                                              <Col sm={6}>
                                                <div className=' mt-2  field-border-brown'>
                                                  <Label className='text-muted'>
                                                    Index Price Reference :
                                                  </Label>
                                                  <p className='form-label fw-semibold fs-12 '>
                                                    {data.indexPricename || '-'}
                                                  </p>
                                                </div>
                                              </Col>

                                              <Col sm={6}>
                                                <div className=' mt-2  field-border-brown'>
                                                  <Label className='text-muted'>
                                                    Other Index Price :
                                                  </Label>
                                                  <p className='form-label fw-semibold fs-12 '>
                                                    {data.otherIndexPricename ||
                                                      '-'}
                                                  </p>
                                                </div>
                                              </Col>

                                              <Col sm={12}>
                                                <div className='mb-2 mt-2'>
                                                  <Label
                                                    className='form-check-label'
                                                    for='inlineCheckbox8'
                                                  >
                                                    {data.indexPricename &&
                                                    data.otherIndexPricename
                                                      ? 'Correlation between two index whichever to lower'
                                                      : ''}
                                                  </Label>
                                                </div>
                                              </Col>
                                              {data.pricingPeriod === 1 && (
                                                <>
                                                  <Col sm={4}>
                                                    <div className='mb-2 mt-2  field-border-brown'>
                                                      <Label className='text-muted'>
                                                        Publish Index Period :
                                                      </Label>
                                                      <p className='form-label mb-2 fs-12 '>
                                                        {formatDateToDDMMYYYY(
                                                          data.startDatePublishIndex
                                                        ) || '-'}
                                                      </p>
                                                    </div>
                                                  </Col>

                                                  <Col sm={4}>
                                                    <div className='mb-2 mt-2  '>
                                                      <Label className='text-muted'>
                                                        {''}
                                                      </Label>
                                                      <p className='form-label mb-2 fs-12 '>
                                                        {formatDateToDDMMYYYY(
                                                          data.endDatePublishIndex
                                                        ) || '-'}
                                                      </p>
                                                    </div>
                                                  </Col>
                                                </>
                                              )}
                                              {data.pricingPeriod === 2 && (
                                                <>
                                                  <Col sm={4}>
                                                    <div className='mb-2 mt-2  field-border-brown'>
                                                      <Label className='text-muted'>
                                                        Schema B/L Date :
                                                      </Label>
                                                      <p className='form-label mb-2 fs-12 '>
                                                        {data.schemaBL || '-'}
                                                      </p>
                                                    </div>
                                                  </Col>
                                                </>
                                              )}
                                            </Row>
                                          </Col>

                                          <Col sm={7}>
                                            <Label
                                              for='product-name-3'
                                              className='form-label '
                                            >
                                              Notes for Pricing
                                            </Label>
                                            <div className='snow-editor '>
                                              <EditorProvider>
                                                <Editor
                                                  id='notesForPricingRO'
                                                  name='notesForPricingRO'
                                                  value={
                                                    data.notesForPricing || ''
                                                  }
                                                  readOnly
                                                  style={{
                                                    height: '165px',
                                                    overflowY: 'auto',
                                                  }}
                                                ></Editor>
                                              </EditorProvider>
                                            </div>
                                          </Col>
                                        </Row>
                                      </Col>
                                    </TabPane>

                                    {/* <TabPane tabId={`${data.id}2`} id='product'>
                                      <Col sm={12}>
                                        <Row>
                                          <Col sm={5}>
                                            <Row>
                                              <Col sm={6}>
                                                <div className='mb-2'>
                                                  <Label className='form-label fw-semibold'>
                                                    Delivery Unit
                                                  </Label>
                                                  <Input
                                                    type='text'
                                                    className='form-control bg-light border-1'
                                                    id='deliveryUnitRO'
                                                    readOnly
                                                    value={
                                                      data.deliveryUnit || ''
                                                    }
                                                  />
                                                </div>
                                              </Col>
                                              <Col sm={6}>
                                                <div className='mb-2'>
                                                  <Label className='form-label '>
                                                    Pricing Period{' '}
                                                  </Label>
                                                  <Input
                                                    type='text'
                                                    className='form-control bg-light border-1'
                                                    id='deliveryUnitRO'
                                                    value={
                                                      PricingPeriodData[0].options.find(
                                                        option =>
                                                          option.value ===
                                                          data.pricingPeriod
                                                      )?.label || ''
                                                    }
                                                    readOnly
                                                  />
                                                </div>
                                              </Col>
                                              <Col sm={6}>
                                                <div className='mb-2'>
                                                  <Label className='form-label '>
                                                    Index Price Reference
                                                  </Label>
                                                  <Input
                                                    type='text'
                                                    className='form-control bg-light border-1 '
                                                    id='indexPricenameRO'
                                                    readOnly
                                                    value={
                                                      data.indexPricename || ''
                                                    }
                                                  />
                                                </div>
                                              </Col>
                                              <Col sm={6}>
                                                <div className='mb-2'>
                                                  <Label className='form-label '>
                                                    Other Index Price
                                                  </Label>
                                                  <Input
                                                    type='text'
                                                    className='form-control bg-light border-1 '
                                                    id='otherIndexPricenameRO'
                                                    readOnly
                                                    value={
                                                      data.otherIndexPricename ||
                                                      ''
                                                    }
                                                  />
                                                </div>
                                              </Col>

                                              {data.isCorrelationBetweenTwoIndex && (
                                                <Col sm={12}>
                                                  <div className='mb-2'>
                                                    <Label className='form-check-label'>
                                                      Correlation between two
                                                      index whicever is lower
                                                    </Label>
                                                  </div>
                                                </Col>
                                              )}
                                              {data.pricingPeriod === 1 && (
                                                <Col sm={4}>
                                                  <div className='mb-2'>
                                                    <Label className='form-label '>
                                                      Publish Index Period :
                                                    </Label>
                                                    <Input
                                                      type='text'
                                                      className='form-control bg-light border-1'
                                                      id='startDatePublishIndexRO'
                                                      readOnly
                                                      value={formatDateToDDMMYYYY(
                                                        data.startDatePublishIndex
                                                      )}
                                                    />
                                                  </div>
                                                </Col>
                                              )}
                                              {data.pricingPeriod === 1 && (
                                                <Col sm={4}>
                                                  <div className='mb-2'>
                                                    <Label className='form-label '>
                                                      to :
                                                    </Label>
                                                    <Input
                                                      type='text'
                                                      className='form-control bg-light border-1  '
                                                      id='endDatePublishIndex'
                                                      readOnly
                                                      value={formatDateToDDMMYYYY(
                                                        data.endDatePublishIndex
                                                      )}
                                                    />
                                                  </div>
                                                </Col>
                                              )}
                                              {data.pricingPeriod === 2 && (
                                                <Col sm={4}>
                                                  <div className='mb-2'>
                                                    <Label className='form-label '>
                                                      Schema B/L Date
                                                    </Label>
                                                    <Input
                                                      type='text'
                                                      className='form-control bg-light border-1 '
                                                      id='totalamountInput'
                                                      readOnly
                                                      value={
                                                        data.schemaBL || ''
                                                      }
                                                    />
                                                  </div>
                                                </Col>
                                              )}
                                              {data.pricingPeriod === 3 && (
                                                <Col sm={4}>
                                                  <div className='mb-2'>
                                                    <Label className='form-label '>
                                                      Price Amount
                                                    </Label>
                                                    <Input
                                                      type='text'
                                                      className='form-control bg-light border-1 '
                                                      id='priceAmount'
                                                      readOnly
                                                      value={
                                                        data.priceAmount || ''
                                                      }
                                                    />
                                                  </div>
                                                </Col>
                                              )}
                                            </Row>
                                          </Col>

                                          <Col sm={7}>
                                            <Label className='form-label '>
                                              Notes for Pricing
                                            </Label>
                                            <div className='snow-editor bg-light '>
                                              <EditorProvider>
                                                <Editor
                                                  id='notesForPricingRO'
                                                  name='notesForPricingRO'
                                                  value={
                                                    data.notesForPricing || ''
                                                  }
                                                  readOnly
                                                  style={{
                                                    height: '200px',
                                                    overflowY: 'auto',
                                                  }}
                                                ></Editor>
                                              </EditorProvider>
                                            </div>
                                          </Col>
                                        </Row>
                                      </Col>
                                    </TabPane> */}
                                  </TabContent>
                                </Col>
                              </Col>
                            ))
                          ) : (
                            <Col lg={12} className='text-center'>
                              <h5 className='fs-15'>No data available</h5>
                            </Col>
                          )}
                        </>
                      )}

                      <Row>
                        <Col sm={12}>
                          <div
                            style={{
                              borderTopColor: '#b0b8b2',
                              borderTopWidth: '1px',
                              borderTopStyle: 'solid',
                            }}
                          ></div>
                          <div className='mb-1  '>
                            <Label
                              id='req-sec-port'
                              for='additionalNotes'
                              className='form-label  text-uppercase fw-semibold'
                            >
                              Port Restriction
                            </Label>

                            <p className='text-muted mb-2 fs-12 '>
                              List port restrictions that apply to this
                              procurement.
                            </p>
                          </div>
                          <div className='mb-2'>
                            <CardBody>
                              <div className=' table-card'>
                                {/* Tabel dibuat manual supaya header MIN/MAX bisa
                                    dikelompokkan di bawah DWT/LOA/DRAFT lewat
                                    colSpan/rowSpan. */}
                                <table className='table table-hover table-centered align-middle table-nowrap mb-0'>
                                  <thead className='table-light'>
                                    <tr>
                                      <th
                                        scope='col'
                                        rowSpan={2}
                                        className='align-middle'
                                        style={{ width: '22%' }}
                                      >
                                        TERMINAL
                                      </th>
                                      <th colSpan={2} className='text-center'>
                                        DWT (TONS)
                                      </th>
                                      <th colSpan={2} className='text-center'>
                                        LOA (M)
                                      </th>
                                      <th colSpan={2} className='text-center'>
                                        DRAFT (M)
                                      </th>
                                    </tr>
                                    <tr>
                                      <th className='text-center'>MIN</th>
                                      <th className='text-center'>MAX</th>
                                      <th className='text-center'>MIN</th>
                                      <th className='text-center'>MAX</th>
                                      <th className='text-center'>MIN</th>
                                      <th className='text-center'>MAX</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {groupedJettyRestrictions.length === 0 && (
                                      <tr>
                                        <td
                                          colSpan={7}
                                          className='text-center text-muted py-3'
                                        >
                                          There is no data port restriction
                                        </td>
                                      </tr>
                                    )}
                                    {groupedJettyRestrictions.map(group => (
                                      <React.Fragment key={group.key}>
                                        <tr>
                                          <td
                                            colSpan={7}
                                            className='fw-semibold'
                                          >
                                            {group.name}
                                          </td>
                                        </tr>
                                        {group.items.map(item => (
                                          <tr key={item.id}>
                                            <td className='ps-4'>
                                              {item.name || 'Jetty Not Found'}
                                            </td>
                                            <td className='text-center'>
                                              {formatNumber(item.dwtMin)}
                                            </td>
                                            <td className='text-center'>
                                              {formatNumber(item.dwtMax)}
                                            </td>
                                            <td className='text-center'>
                                              {formatNumber(item.loaMin)}
                                            </td>
                                            <td className='text-center'>
                                              {formatNumber(item.loaMax)}
                                            </td>
                                            <td className='text-center'>
                                              {formatNumber(item.draftMin)}
                                            </td>
                                            <td className='text-center'>
                                              {formatNumber(item.draftMax)}
                                            </td>
                                          </tr>
                                        ))}
                                      </React.Fragment>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </CardBody>
                          </div>
                        </Col>

                        <Col sm={12}>
                          <div
                            style={{
                              borderTopColor: '#b0b8b2',
                              borderTopWidth: '1px',
                              borderTopStyle: 'solid',
                            }}
                          ></div>
                          <Card>
                            <div className='mb-1  '>
                              <Label
                                for='additionalNotes'
                                className='form-label  text-uppercase fw-semibold'
                              >
                                Notes
                              </Label>

                              <p className='text-muted mb-2 fs-12 '>
                                Add supporting notes for the port restriction
                                above.
                              </p>
                            </div>
                            <div className='snow-editor '>
                              <EditorProvider>
                                <Editor
                                  ref={editorRef}
                                  id='globalNotes'
                                  name='globalNotes'
                                  value={form.globalNotes || ''}
                                  onChange={e => {
                                    handleNoteTextChange(e, 'globalNotes');
                                  }}
                                  style={{ height: '200px', overflowY: 'auto' }}
                                  className={
                                    form.isProcurementCompleted === false
                                      ? 'form-control'
                                      : 'form-control bg-light border-0'
                                  }
                                >
                                  {form.isProcurementCompleted === false && (
                                    <Toolbar>
                                      <BtnBold />
                                      <BtnItalic />
                                      <BtnBulletList />
                                      <BtnClearFormatting />
                                      <BtnLink />
                                      <BtnNumberedList />
                                      <BtnRedo />
                                      <BtnStrikeThrough />
                                      <BtnStyles />
                                      <HighlightColor />
                                      <BtnUnderline />
                                      <BtnUndo />
                                      <Separator />
                                    </Toolbar>
                                  )}
                                </Editor>
                              </EditorProvider>
                            </div>
                          </Card>
                        </Col>
                      </Row>

                      {/* </CardBody> */}
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Card className='border shadow-sm'>
              <CardBody>
                <Row>
                  <Col sm={12}>
                    <div className='d-flex align-items-start justify-content-between mb-3'>
                      <div>
                        <Label
                          id='req-sec-clause'
                          for='clauseDetails'
                          className='form-label  text-uppercase fw-semibold'
                        >
                          Clause Details
                        </Label>
                        <p className='text-muted mb-0 fs-12 '>
                          Click a clause to expand and edit its wording.
                        </p>
                      </div>
                      <span className='badge bg-primary-subtle text-primary'>
                        {dataCause.items.length} clause
                        {dataCause.items.length === 1 ? '' : 's'}
                      </span>
                    </div>
                  </Col>
                </Row>

                {dataCause.items.map((item, index) => {
                  const isMandatory =
                    dataCause.procurementClauseDetails?.find(
                      d => String(d.id) === String(item.id)
                    )?.isShowToDMUT ??
                    item.isShowToDMUT ??
                    false;
                  const isOpen =
                    Boolean(expandedClauses[item.id]) ||
                    Boolean(errors[item.id]);

                  return (
                    <div className='clause-card' key={`${item.id}-${index}`}>
                      <button
                        type='button'
                        className='clause-card-header'
                        onClick={() => toggleClauseCard(item.id)}
                        aria-expanded={isOpen}
                      >
                        <span className='clause-card-icon'>
                          <i className='ri-article-line'></i>
                        </span>
                        <span className='clause-card-title'>
                          {item.clauseTitle}
                        </span>
                        {isMandatory && (
                          <span className='badge bg-danger-subtle text-danger'>
                            Mandatory for DMUT
                          </span>
                        )}
                        {errors[item.id] && (
                          <span className='badge bg-warning-subtle text-warning'>
                            Needs attention
                          </span>
                        )}
                        <i
                          className={`ri-arrow-down-s-line clause-chevron ${
                            isOpen ? 'open' : ''
                          }`}
                        ></i>
                      </button>

                      {isOpen && (
                        <div className='clause-card-body'>
                          <div className='d-flex form-check form-switch form-switch-secondary mb-3'>
                            <div className='flex-shrink-0'>
                              <Input
                                className='form-check-input'
                                type='checkbox'
                                role='switch'
                                id={`isShowToDMUT_${item.id}`}
                                name={`isShowToDMUT_${item.id}`}
                                checked={isMandatory}
                                onChange={e => {
                                  handleClauseCheckBoxChange(e);
                                }}
                                disabled={form.isProcurementCompleted !== false}
                              />
                            </div>
                            <Label className='form-label ms-2 mb-0'>
                              Mandatory clause for DMUT to fulfill
                            </Label>
                          </div>
                          {errors[item.id] && (
                            <div className='text-danger d-flex align-items-center gap-1 mb-2'>
                              <i className='mdi mdi-alert-circle-outline'></i>
                              <span>{errors[item.id]}</span>
                            </div>
                          )}
                          <div className='snow-editor '>
                            <EditorProvider>
                              <Editor
                                id={item.id}
                                name={item.id}
                                value={
                                  dataCause.procurementClauseDetails?.find(
                                    d => String(d.id) === String(item.id)
                                  )?.clauseDescription ??
                                  item.clauseDescription ??
                                  ''
                                }
                                onChange={e => {
                                  handleNoteTextChanges(
                                    e,
                                    `clauseDesc_${item.id}`
                                  );
                                  setErrors(prev => ({
                                    ...prev,
                                    [item.id]: '',
                                  }));
                                }}
                                style={{ height: '200px', overflowY: 'auto' }}
                                className={
                                  form.isProcurementCompleted === false
                                    ? 'form-control'
                                    : 'form-control bg-light border-0'
                                }
                              >
                                {form.isProcurementCompleted === false && (
                                  <Toolbar>
                                    <BtnBold />
                                    <BtnItalic />
                                    <BtnBulletList />
                                    <BtnClearFormatting />
                                    <BtnLink />
                                    <BtnNumberedList />
                                    <BtnRedo />
                                    <BtnStrikeThrough />
                                    <BtnStyles />

                                    <BtnUnderline />
                                    <HighlightColor />
                                    <BtnUndo />

                                    <Separator />
                                    <select
                                      className='form-select form-select-sm'
                                      style={{
                                        width: '210px',
                                        fontSize: '12px',
                                      }}
                                      value={insertOption}
                                      onChange={e => {
                                        handleDropdownInsert(
                                          e.target.value,
                                          item.id
                                        );
                                        const editorEl =
                                          document.getElementById(item.id);
                                        if (editorEl) {
                                          handleNoteTextChanges(
                                            {
                                              target: {
                                                value: editorEl.innerHTML,
                                              },
                                            },
                                            `clauseDesc_${item.id}`
                                          );
                                        }
                                      }}
                                    >
                                      <option value=''>-Add Template-</option>
                                      {(
                                        templatesByClauseDetailId[
                                          item.clauseDetailId
                                        ] || []
                                      ).map(tpl => (
                                        <option key={tpl.id} value={tpl.id}>
                                          {tpl.name}
                                        </option>
                                      ))}
                                    </select>
                                  </Toolbar>
                                )}
                              </Editor>
                            </EditorProvider>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardBody>
            </Card>

            <Card className='border shadow-sm'>
              <CardBody>
                <Row>
                  <Col sm={12}>
                    <div>
                      <div className='flex-grow-1'>
                        <Label
                          id='req-sec-payment'
                          className='form-label  text-uppercase fw-semibold'
                        >
                          Payment Terms
                        </Label>

                        <p className='text-muted mb-2 fs-12 '>
                          Choose the payment methods and terms that apply.
                        </p>
                      </div>
                    </div>
                  </Col>

                  <Col sm={4}>
                    <div className='mb-2'>
                      <Label for='invoicingQuantityId' className='form-label '>
                        Invoicing Quantity
                      </Label>

                      <Select
                        name='invoicingQuantityId'
                        id='invoicingQuantityId'
                        options={invoiceQuantityData.items}
                        placeholder='Please Select'
                        value={
                          invoiceQuantityData.items?.find(
                            obj => obj.value === form.invoicingQuantityId
                          ) || null
                        }
                        onChange={e => {
                          handleSelectChange(e, 'invoicingQuantityId');
                        }}
                        isDisabled={form.isProcurementCompleted}
                        styles={{
                          singleValue: base => ({
                            ...base,
                            color: '#000',
                          }),
                          input: base => ({
                            ...base,
                            color: '#000',
                          }),
                        }}
                      />
                    </div>
                  </Col>

                  <Col sm={4}>
                    <div className='mb-2'>
                      <Label
                        for='modeInvoicingQuantityId'
                        className='form-label text-muted'
                      >
                        Mode
                      </Label>

                      <Select
                        name='modeInvoicingQuantityId'
                        id='modeInvoicingQuantityId'
                        options={modelinvoiceQuantityData.items}
                        placeholder='Please Select'
                        value={
                          modelinvoiceQuantityData.items?.find(
                            obj => obj.value === form.modeInvoicingQuantityId
                          ) || null
                        }
                        onChange={e => {
                          handleSelectChange(e, 'modeInvoicingQuantityId');
                        }}
                        isDisabled={form.isProcurementCompleted}
                        styles={{
                          singleValue: base => ({
                            ...base,
                            color: '#000',
                          }),
                          input: base => ({
                            ...base,
                            color: '#000',
                          }),
                        }}
                      />
                    </div>
                  </Col>

                  <Col sm={4}>
                    <div className='mb-2'>
                      <Label
                        for='paymentCurrencyId'
                        className='form-label text-muted'
                      >
                        Currency
                      </Label>

                      <Select
                        name='paymentCurrencyId'
                        id='paymentCurrencyId'
                        options={currencyName.items}
                        placeholder='Please Select'
                        value={
                          currencyName.items?.find(
                            obj => obj.value === form.paymentCurrencyId
                          ) || null
                        }
                        onChange={e => {
                          handleSelectChange(e, 'paymentCurrencyId');
                        }}
                        isDisabled={form.isProcurementCompleted}
                        styles={{
                          singleValue: base => ({
                            ...base,
                            color: '#000',
                          }),
                          input: base => ({
                            ...base,
                            color: '#000',
                          }),
                        }}
                      />
                    </div>
                  </Col>

                  <Col sm={12}>
                    <Card>
                      <Label
                        for='descriptionOfPaymentTerms'
                        className='form-label  text-uppercase fw-semibold'
                      >
                        Description of Payment Term
                      </Label>
                      {errors.descriptionOfPaymentTerms && (
                        <div className='text-danger d-flex align-items-center gap-1'>
                          <i className='mdi mdi-alert-circle-outline'></i>
                          <span>{errors.descriptionOfPaymentTerms}</span>
                        </div>
                      )}{' '}
                      <EditorProvider>
                        <Editor
                          id='descriptionOfPaymentTerms'
                          name='descriptionOfPaymentTerms'
                          value={form.descriptionOfPaymentTerms || ''}
                          //onChange={handleTextChange("descriptionOfPaymentTerms")}
                          onChange={e => {
                            handleNoteTextChange(
                              e,
                              'descriptionOfPaymentTerms'
                            );
                            setErrors(prev => ({
                              ...prev,
                              descriptionOfPaymentTerms: '',
                            }));
                          }}
                          style={{ height: '200px', overflowY: 'auto' }}
                          className={
                            form.isProcurementCompleted === false
                              ? 'form-control'
                              : 'form-control bg-light border-0'
                          }
                        >
                          {form.isProcurementCompleted === false && (
                            <Toolbar>
                              <BtnBold />
                              <BtnItalic />
                              <BtnBulletList />
                              <BtnClearFormatting />
                              <BtnLink />
                              <BtnNumberedList />
                              <BtnRedo />
                              <BtnStrikeThrough />
                              <BtnStyles />
                              <BtnUnderline />
                              <BtnUndo />

                              <Separator />
                              <HighlightColor />
                            </Toolbar>
                          )}
                        </Editor>
                      </EditorProvider>
                    </Card>
                  </Col>
                </Row>

                <Row>
                  <Col sm={12}>
                    <div
                      style={{
                        borderTopColor: '#b0b8b2',
                        borderTopWidth: '1px',
                        borderTopStyle: 'solid',
                      }}
                    >
                      <div className='flex-grow-1'>
                        <Label
                          id='req-sec-compliance'
                          className='form-label  text-uppercase fw-semibold'
                        >
                          Compliance
                        </Label>

                        <p className='text-muted mb-2 fs-12 '>
                          Confirm the compliance requirements for this
                          procurement.
                        </p>
                      </div>
                    </div>
                  </Col>

                  <Col sm={4}>
                    <div className='mb-2'>
                      <Label for='lawId' className='form-label '>
                        Law
                      </Label>
                      <Select
                        name='lawId'
                        id='lawId'
                        options={lawData.items}
                        placeholder='Please Select'
                        value={
                          lawData.items?.find(
                            obj => obj.value === form.lawId
                          ) || null
                        }
                        onChange={e => {
                          handleSelectChange(e, 'lawId');
                        }}
                        isDisabled={form.isProcurementCompleted}
                        styles={{
                          singleValue: base => ({
                            ...base,
                            color: '#000',
                          }),
                          input: base => ({
                            ...base,
                            color: '#000',
                          }),
                        }}
                      />
                    </div>
                  </Col>

                  <Col sm={4}>
                    <div className='mb-2'>
                      <Label for='arbitrationId' className='form-label '>
                        Abitration
                      </Label>

                      <Select
                        name='arbitrationId'
                        id='arbitrationId'
                        options={abitrationData.items}
                        placeholder='Please Select'
                        value={
                          abitrationData.items?.find(
                            obj => obj.value === form.arbitrationId
                          ) || null
                        }
                        onChange={e => {
                          handleSelectChange(e, 'arbitrationId');
                        }}
                        isDisabled={form.isProcurementCompleted}
                        styles={{
                          singleValue: base => ({
                            ...base,
                            color: '#000',
                          }),
                          input: base => ({
                            ...base,
                            color: '#000',
                          }),
                        }}
                      />
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col sm={12}>
                    <div
                      style={{
                        borderTopColor: '#b0b8b2',
                        borderTopWidth: '1px',
                        borderTopStyle: 'solid',
                      }}
                    >
                      <div className='flex-grow-1'>
                        <Label
                          id='req-sec-other'
                          for='otherTermsAndCondition'
                          className='form-label  text-uppercase fw-semibold'
                        >
                          Other Terms & Condition
                        </Label>
                        {errors.otherTermsAndCondition && (
                          <div className='text-danger d-flex align-items-center gap-1'>
                            <i className='mdi mdi-alert-circle-outline'></i>
                            <span>{errors.otherTermsAndCondition}</span>
                          </div>
                        )}{' '}
                      </div>
                    </div>
                  </Col>

                  <Col sm={12}>
                    <Card>
                      <div className='snow-editor '>
                        <EditorProvider>
                          <Editor
                            id='otherTermsAndCondition'
                            name='otherTermsAndCondition'
                            value={form.otherTermsAndCondition || ''}
                            onChange={e => {
                              handleNoteTextChange(e, 'otherTermsAndCondition');
                              setErrors(prev => ({
                                ...prev,
                                otherTermsAndCondition: '',
                              }));
                            }}
                            style={{ height: '200px', overflowY: 'auto' }}
                            className={
                              form.isProcurementCompleted === false
                                ? 'form-control'
                                : 'form-control bg-light border-0'
                            }
                          >
                            {form.isProcurementCompleted === false && (
                              <Toolbar>
                                <BtnBold />
                                <BtnItalic />
                                <BtnBulletList />
                                <BtnClearFormatting />
                                <BtnLink />
                                <BtnNumberedList />

                                <BtnRedo />
                                <BtnStrikeThrough />
                                <BtnStyles />
                                <BtnUnderline />
                                <BtnUndo />

                                <Separator />
                                <HighlightColor />
                                {/* <select
                                  className='form-select form-select-sm'
                                  style={{ width: '210px', fontSize: '12px' }}
                                  value={insertOption}
                                  onChange={e =>
                                    handleDropdownInsert(
                                      e.target.value,
                                      'otherTermsAndCondition'
                                    )
                                  }
                                >
                                  <option value=''>-Add Template-</option>
                                  {templateOptions.map(tpl => (
                                    <option key={tpl.id} value={tpl.id}>
                                      {tpl.name}
                                    </option>
                                  ))}
                                </select> */}
                              </Toolbar>
                            )}
                          </Editor>
                        </EditorProvider>
                      </div>
                    </Card>
                  </Col>
                </Row>

                <Col sm={12} className='mb-2'>
                  <div
                    style={{
                      borderTopColor: '#b0b8b2',
                      borderTopWidth: '1px',
                      borderTopStyle: 'solid',
                    }}
                  >
                    <div className='flex-grow-1'>
                      <Label
                        for='globalNotes'
                        className='form-label  text-uppercase fw-semibold'
                      >
                        Additional Notes
                      </Label>
                    </div>
                  </div>
                </Col>

                <Col sm={12}>
                  <Card>
                    <div className='snow-editor '>
                      <EditorProvider>
                        <Editor
                          id='additionalNotes'
                          name='additionalNotes'
                          value={form.additionalNotes || ''}
                          onChange={e => {
                            handleNoteTextChange(e, 'additionalNotes');
                          }}
                          //onChange={handleTextChange('additionalNotes')}
                          style={{ height: '200px', overflowY: 'auto' }}
                          className={
                            form.isProcurementCompleted === false
                              ? 'form-control'
                              : 'form-control bg-light border-0'
                          }
                        >
                          {form.isProcurementCompleted === false && (
                            <Toolbar>
                              <BtnBold />
                              <BtnItalic />
                              <BtnBulletList />
                              <BtnClearFormatting />
                              <BtnLink />
                              <BtnNumberedList />
                              <BtnRedo />
                              <BtnStrikeThrough />
                              <BtnStyles />
                              <BtnUnderline />
                              <BtnUndo />

                              <Separator />
                              <HighlightColor />
                            </Toolbar>
                          )}
                        </Editor>
                      </EditorProvider>
                    </div>
                  </Card>
                </Col>
              </CardBody>
            </Card>

            <Card className='border shadow-sm'>
              <CardBody>
                <Row>
                  <Col sm={12}>
                    <div className='mb-2 bor border-top  border-top-dashed '>
                      <Label
                        id='req-sec-validity'
                        className='form-label  text-uppercase fw-semibold'
                      >
                        Validity
                      </Label>
                      <p className='text-muted mb-2 fs-12 '>
                        Set how long the submitted proposal remains valid.
                      </p>
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col sm={6}>
                    <Row>
                      <Col sm={6}>
                        <div className='mb-2'>
                          <Label
                            for='proposalSubmissionDeadline'
                            className='form-label fw-semibold'
                          >
                            Submission Deadline (Jakarta Local Time)
                          </Label>
                          <Flatpickr
                            id='proposalSubmissionDeadline'
                            name='proposalSubmissionDeadline'
                            className={
                              form.isProcurementCompleted === false
                                ? 'form-control'
                                : 'form-control bg-light border-1'
                            }
                            options={{
                              enableTime: true,
                              dateFormat: 'd/m/Y H:i', // Format tampilan
                              time_24hr: true,
                            }}
                            value={form.proposalSubmissionDeadline || ''}
                            //onChange={handleDateChange}
                            onChange={handleDateChange(
                              'proposalSubmissionDeadline',
                              true
                            )}
                            disabled={
                              form.isProcurementCompleted ? true : false
                            }
                          />
                          {errors.proposalSubmissionDeadline && (
                            <div className='text-danger d-flex align-items-center gap-1'>
                              <i className='mdi mdi-alert-circle-outline'></i>
                              <span>{errors.proposalSubmissionDeadline}</span>
                            </div>
                          )}{' '}
                        </div>
                      </Col>

                      <Col sm={6}>
                        <div className='mb-2'>
                          <Label
                            for='proposalValidityPeriodMax'
                            className='form-label fw-semibold'
                          >
                            Validity Period (Jakarta Local Time)
                          </Label>
                          <Flatpickr
                            id='proposalValidityPeriodMax'
                            name='proposalValidityPeriodMax'
                            className={
                              form.isProcurementCompleted === false
                                ? 'form-control'
                                : 'form-control bg-light border-1'
                            }
                            options={{
                              enableTime: true,
                              dateFormat: 'd/m/Y H:i', // Format tampilan
                              time_24hr: true,
                            }}
                            value={form.proposalValidityPeriodMax || ''}
                            //onChange={handleDateChange}
                            onChange={handleDateChange(
                              'proposalValidityPeriodMax',
                              true
                            )}
                            disabled={
                              form.isProcurementCompleted ? true : false
                            }
                          />
                          {errors.proposalValidityPeriodMax && (
                            <div className='text-danger d-flex align-items-center gap-1'>
                              <i className='mdi mdi-alert-circle-outline'></i>
                              <span>{errors.proposalValidityPeriodMax}</span>
                            </div>
                          )}{' '}
                        </div>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {form.isProcurementCompleted === false && (
              <div className='req-actionbar'>
                <button
                  type='button'
                  className='btn btn-light btn-label'
                  onClick={() => {
                    if (validatePublishIndexDatesOnSave()) {
                      handleSaveDraftProc();
                    }
                  }}
                >
                  <i className='ri-stack-line label-icon align-middle fs-16 me-2'></i>
                  Save as Draft
                </button>
                <button
                  type='button'
                  className='btn btn-primary btn-label right nexttab'
                  onClick={() => {
                    if (validatePublishIndexDatesOnSave()) {
                      handleSaveCompleteProc();
                    }
                  }}
                >
                  <i className='ri-login-box-line label-icon align-middle fs-16 ms-2'></i>
                  Save & Complete
                </button>
              </div>
            )}
          </div>
        </div>
      </Row>
      <Modal
        isOpen={deletemodal}
        role='dialog'
        autoFocus={true}
        centered
        id='removeItemModal'
        toggle={toggledeletemodal}
      >
        <ModalHeader
          toggle={() => {
            setDeleteModal(!deletemodal);
          }}
        ></ModalHeader>
        <ModalBody>
          <div className='mt-2 text-center'>
            <div className='mt-4 pt-2 fs-15 mx-4 mx-sm-5'>
              <h4>Are you Sure ?</h4>
              <p className='text-muted mx-4 mb-0'>
                Are you Sure You want to Remove this Address ?
              </p>
            </div>
          </div>
          <div className='d-flex gap-2 justify-content-center mt-4 mb-2'>
            <button
              type='button'
              className='btn w-sm btn-light'
              onClick={() => {
                setDeleteModal(!deletemodal);
              }}
            >
              Close
            </button>
            <button
              type='button'
              className='btn w-sm btn-danger'
              onClick={() => {
                setDeleteModal(!deletemodal);
              }}
            >
              Yes, Delete It!
            </button>
          </div>
        </ModalBody>
      </Modal>
      {/* //---- Modal quantity */}
      {/* modal Add Address */}

      <Modal
        //size="lg"
        isOpen={modal}
        role='dialog'
        autoFocus={true}
        centered
        id='addAddressModal'
        toggle={deliverymodal}
        data-bs-backdrop='static'
        //size='xl'
        className='modal-custom-xl'
      >
        <ModalHeader
          toggle={() => {
            setModal(!modal);
          }}
        >
          <span className='modal-title' id='addAddressModalLabel'>
            Quantity And Delivery Time
          </span>
        </ModalHeader>

        <ModalBody>
          <Row className='mb-3' style={{ minHeight: '485px' }}>
            <Col sm={12}>
              <CardBody>
                <div className='flex-grow-1'>
                  <p className='text-muted mb-2 fs-12 '>
                    Manage the delivery schedule and quantity for each parcel.
                  </p>
                </div>

                <Nav tabs className='nav-tabs mb-3'>
                  <NavItem>
                    <NavLink
                      style={{ cursor: 'pointer' }}
                      className={classnames({ active: activeTab === '1' })}
                      onClick={() => {
                        toggles('1');
                      }}
                    >
                      <label className='text-uppercase'>
                        {' '}
                        Quantity Delivery Time
                      </label>
                    </NavLink>
                  </NavItem>
                  {/* {deliveryformData.id !== null ? (
                  <> */}
                  <NavItem>
                    <NavLink
                      style={{ cursor: 'pointer' }}
                      className={classnames({ active: activeTab === '2' })}
                      onClick={() => {
                        toggles('2');
                      }}
                    >
                      <label className='text-uppercase'> Pricing</label>
                    </NavLink>
                  </NavItem>
                  {/* </>
                  ) : null} */}
                </Nav>

                <TabContent activeTab={activeTab}>
                  <TabPane tabId='1' id='home'>
                    <Col sm={12}>
                      <Row>
                        <Col sm={6}>
                          <div className='mb-2'>
                            <Label
                              for='deliveryUnit'
                              className='form-label fw-semibold '
                            >
                              Delivery Unit :
                            </Label>

                            <Input
                              type='text'
                              id='deliveryUnit'
                              name='deliveryUnit'
                              value={deliveryformData.deliveryUnit || ''}
                              className={
                                form.isProcurementCompleted
                                  ? 'form-control bg-light border-1'
                                  : 'form-control'
                              }
                              onChange={
                                form.isProcurementCompleted
                                  ? undefined
                                  : e => {
                                      handleDeliveryTextChange(e);
                                      setErrors(prev => ({
                                        ...prev,
                                        deliveryUnit: '',
                                      }));
                                    }
                              }
                              readOnly={form.isProcurementCompleted}
                            />
                          </div>
                          {errors.deliveryUnit && (
                            <div className='text-danger d-flex align-items-center gap-1'>
                              <i className='mdi mdi-alert-circle-outline'></i>
                              <span>{errors.deliveryUnit}</span>
                            </div>
                          )}{' '}
                        </Col>
                        {!LPG_PRODUCT.includes(form.productNameId) && (
                          <>
                            <Col sm={2}>
                              <div className='mb-2'>
                                <Label for='frequency' className='form-label '>
                                  Frequency :
                                </Label>
                                <Input
                                  type='number'
                                  id='frequency'
                                  name='frequency'
                                  className='form-control'
                                  value={deliveryformData.frequency || ''}
                                  onChange={e => {
                                    handleDeliveryTextChange(e);
                                    setErrors(prev => ({
                                      ...prev,
                                      frequency: '',
                                    }));
                                  }}
                                />
                              </div>
                              {errors.frequency && (
                                <div className='text-danger d-flex align-items-center gap-1'>
                                  <i className='mdi mdi-alert-circle-outline'></i>
                                  <span>{errors.frequency}</span>
                                </div>
                              )}{' '}
                            </Col>
                          </>
                        )}

                        {LPG_PRODUCT.includes(form.productNameId) &&
                          TARGET_PRODUCT_TYPES_MIX.includes(
                            form.productTypeId
                          ) && (
                            <>
                              <Col sm={3}>
                                <div className='mb-2'>
                                  <p className='form-label '>Cargo Type : </p>
                                  <Select
                                    id='cargoType'
                                    name='cargoType'
                                    options={[{ options: cargoTypeOptions }]}
                                    placeholder='Please Select'
                                    isClearable
                                    value={
                                      cargoTypeOptions.find(
                                        option =>
                                          option.value ===
                                          (TARGET_PRODUCT_TYPES_MIX.includes(
                                            form.productTypeId
                                          )
                                            ? deliveryformData.cargoType
                                            : form.cargoType)
                                      ) || null
                                    }
                                    onChange={e => {
                                      handleDeliverySelectChange(
                                        e,
                                        'cargoType'
                                      );
                                      generateQtyDeliveryTotalQtyByCargoType(
                                        e ? e.value : null
                                      );
                                    }}
                                    isDisabled={
                                      form.isProcurementCompleted ||
                                      qtyDeliveryDataIncoterm.items.length > 0
                                    }
                                  />
                                </div>
                              </Col>
                            </>
                          )}
                      </Row>
                    </Col>

                    {/* {deliveryformData.id !== null ? ( */}
                    <Row className='mb-3 '>
                      <Col sm={3}>
                        <Row>
                          <Col sm={12}>
                            <div className='d-flex align-items-center mb-2  border-top  border-top-dashed'>
                              <div className='flex-grow-1 '>
                                <Label className='form-label   fw-semibold'>
                                  Quantity
                                </Label>
                              </div>
                              <div className='flex-shrink-0'>
                                {form.isProcurementCompleted === false &&
                                  !LPG_PRODUCT.includes(form.productNameId) &&
                                  qtyDeliveryDataIncoterm.items.length ===
                                    0 && (
                                    <button
                                      type='button'
                                      className='btn btn-sm btn-success mb-2  btn-label right ms-auto nexttab'
                                      onClick={() =>
                                        handleDeliveryQuantityOpenModalNew()
                                      }
                                    >
                                      <i className='ri-function-line label-icon align-middle fs-16 ms-2'></i>
                                      Add Quantity
                                    </button>
                                  )}
                              </div>
                            </div>
                          </Col>

                          <div className='d-flex'>
                            <table className='table table-hover table-centered align-middle table-nowrap mb-2'>
                              <thead className='text-muted table-light'>
                                <tr>
                                  <th scope='col' width='75%' className='py-2'>
                                    {unitSizeType}
                                  </th>
                                  <th
                                    scope='col'
                                    width='25%'
                                    className='py-2 text-center'
                                  >
                                    Action
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {qtyDeliveryDataTotalQty.items.map(
                                  (data, index) => (
                                    <tr key={`${data.id}-${index}`}>
                                      <td className='py-1'>
                                        {formatNumber(data.quantity)}
                                      </td>

                                      <td className='py-1 text-center'>
                                        {form.isProcurementCompleted ===
                                          false &&
                                          !LPG_PRODUCT.includes(
                                            form.productNameId
                                          ) &&
                                          qtyDeliveryDataIncoterm.items
                                            .length === 0 && (
                                            <>
                                              <button
                                                type='button'
                                                className='btn btn-soft-info btn-sm btn-icon me-1'
                                                onClick={() =>
                                                  handleEditQuantityItem(index)
                                                }
                                              >
                                                <i className='ri-pencil-fill align-bottom'></i>
                                              </button>
                                              <button
                                                type='button'
                                                className='btn btn-soft-danger btn-sm btn-icon'
                                                onClick={() =>
                                                  handleDeleteQuantityItem(
                                                    index
                                                  )
                                                }
                                              >
                                                <i className='ri-delete-bin-6-line align-bottom'></i>
                                              </button>
                                            </>
                                          )}
                                      </td>
                                    </tr>
                                  )
                                )}
                              </tbody>
                            </table>
                          </div>
                        </Row>
                      </Col>

                      <Col sm={9}>
                        <Row>
                          <Col sm={12}>
                            <div className='d-flex align-items-center mb-2  bor border-top  border-top-dashed'>
                              <div className='flex-grow-1'>
                                <Label className='form-label  text-uppercase fw-semibold'>
                                  Incoterm
                                </Label>
                              </div>
                              <div className='flex-shrink-0'>
                                {form.isProcurementCompleted === false &&
                                  deliveryformData.id !== null && (
                                    <button
                                      type='button'
                                      className='btn btn-sm btn-success mb-1 mt-2 btn-label right ms-auto nexttab'
                                      onClick={() =>
                                        handleIncotermOpenModalNew(
                                          deliveryformData.id
                                        )
                                      }
                                    >
                                      <i className=' ri-map-pin-line label-icon align-middle fs-16 ms-2 '></i>
                                      Add Incoterm
                                    </button>
                                  )}
                              </div>
                            </div>
                          </Col>

                          <div className='d-flex'>
                            <table className='table table-hover table-centered align-middle table-nowrap mb-2'>
                              <thead className='text-muted table-light'>
                                <tr>
                                  <th scope='col' width='80%' className='py-2'>
                                    Delivery
                                  </th>
                                  <th
                                    scope='col'
                                    width='10%'
                                    className='py-2'
                                  ></th>
                                  <th scope='col' width='10%' className='py-2'>
                                    Action
                                  </th>
                                </tr>
                              </thead>

                              <tbody>
                                {qtyDeliveryDataIncoterm.items.map(
                                  (data, index) => (
                                    <tr key={`${data.id}-${index}`}>
                                      <td>
                                        {(() => {
                                          const portLocations = [];

                                          if (
                                            Array.isArray(
                                              data.incotermFOBLocations
                                            ) &&
                                            data.incotermFOBLocations.length > 0
                                          ) {
                                            const names =
                                              data.incotermFOBLocations
                                                .map(loc => loc.name)
                                                .join(' or ');
                                            portLocations.push({
                                              location: `${data.routes} ${names}`,
                                              startDate: data.startDate,
                                              endDate: data.endDate,
                                            });
                                          }

                                          if (
                                            data.incotermLocation != null &&
                                            data.incotermLocation !== ''
                                          ) {
                                            portLocations.push({
                                              location: data.incotermLocation,
                                              startDate: data.startDate,
                                              endDate: data.endDate,
                                            });
                                          }
                                          if (
                                            data.incotermLocation2 != null &&
                                            data.incotermLocation2 !== ''
                                          ) {
                                            portLocations.push({
                                              location: data.incotermLocation2,
                                              startDate: data.startDate2,
                                              endDate: data.endDate2,
                                            });
                                          }
                                          if (
                                            data.incotermLocation3 != null &&
                                            data.incotermLocation3 !== ''
                                          ) {
                                            portLocations.push({
                                              location: data.incotermLocation3,
                                              startDate: data.startDate3,
                                              endDate: data.endDate3,
                                            });
                                          }
                                          return portLocations.map(
                                            (item, idx) => (
                                              <p
                                                className='mb-1'
                                                key={idx}
                                                style={{
                                                  whiteSpace: 'pre-wrap',
                                                  wordBreak: 'break-word',
                                                }}
                                              >
                                                {data.incoterm}{' '}
                                                {data.incotermPortType}{' '}
                                                {data.incoterm === 'FOB' &&
                                                Array.isArray(
                                                  data.incotermFOBLocations
                                                ) &&
                                                data.incotermFOBLocations
                                                  .length > 3
                                                  ? `${data.routes} all ${data.routes}`
                                                  : item.location}
                                                ,{' on '}
                                                {data.timelineType}{' '}
                                                {item.startDate} -{' '}
                                                {item.endDate}
                                              </p>
                                            )
                                          );
                                        })()}
                                      </td>
                                      <td>
                                        {Array.isArray(
                                          data.incotermFOBLocations
                                        ) &&
                                          data.incotermFOBLocations.length >
                                            3 && (
                                            <Button
                                              color='link'
                                              className='p-0 text-decoration-none'
                                              onClick={() =>
                                                handleViewOrigin(
                                                  data.incotermFOBLocations,
                                                  data.routes
                                                )
                                              }
                                            >
                                              {`View ${data.routes}`}
                                            </Button>
                                          )}
                                      </td>
                                      <td>
                                        {/* <ActionMenu /> */}

                                        <ul className='list-inline hstack gap-2 mb-0 '>
                                          {form.isProcurementCompleted ===
                                            false && (
                                            <>
                                              <Button
                                                id={`editBtn-${data.id}`}
                                                color='primary'
                                                className='btn-icon btn-sm'
                                                outline
                                                onClick={() =>
                                                  handleIncotermOpenModal(
                                                    data.id,
                                                    data.incotermId,
                                                    'incoterm'
                                                  )
                                                }
                                              >
                                                {' '}
                                                <i className=' ri-file-list-2-line' />{' '}
                                              </Button>
                                              <UncontrolledTooltip
                                                placement='top'
                                                target={`editBtn-${data.id}`}
                                                fade={true}
                                                delay={{ show: 0, hide: 0 }}
                                              >
                                                Edit incoterm
                                              </UncontrolledTooltip>
                                            </>
                                          )}

                                          {form.isProcurementCompleted ===
                                            false && (
                                            <>
                                              <Button
                                                id={`deleteBtn-${data.id}`}
                                                color='danger'
                                                className='btn-icon btn-sm'
                                                outline
                                                onClick={() =>
                                                  handleDeleteIncotermDeliveryDetail(
                                                    data.id,
                                                    data.incotermId,
                                                    'incoterm'
                                                  )
                                                }
                                              >
                                                {' '}
                                                <i className='ri-delete-bin-5-line' />{' '}
                                              </Button>

                                              <UncontrolledTooltip
                                                placement='top'
                                                target={`deleteBtn-${data.id}`}
                                                fade={true}
                                                delay={{ show: 0, hide: 0 }}
                                              >
                                                Delete incoterm
                                              </UncontrolledTooltip>
                                            </>
                                          )}
                                        </ul>
                                      </td>
                                    </tr>
                                  )
                                )}
                              </tbody>

                              {/* {qtyDeliveryDataIncoterm.items.length > 0 && (
                                <tfoot className='table-light'>
                                  <tr>
                                    <td colSpan={3} className='py-2'>
                                      <span className='fw-semibold'>
                                        Total Location FOB:
                                      </span>{' '}
                                      {incotermLocationTotals.fob}
                                      <span className='fw-semibold ms-4'>
                                        Total Location:
                                      </span>{' '}
                                      {incotermLocationTotals.location}
                                    </td>
                                  </tr>
                                </tfoot>
                              )} */}
                            </table>
                          </div>
                        </Row>
                      </Col>
                    </Row>
                  </TabPane>

                  <TabPane tabId='2' id='product'>
                    <Col sm={12}>
                      <Row>
                        <Col sm={12}>
                          <Row>
                            <Col sm={4}>
                              <div className='mb-2'>
                                <Label className='form-label fw-semibold'>
                                  Pricing Period
                                </Label>
                                <Select
                                  id='pricingPeriod'
                                  name='pricingPeriod'
                                  options={PricingPeriodData}
                                  placeholder='Please Select'
                                  isClearable
                                  value={
                                    PricingPeriodData[0].options.find(
                                      option =>
                                        option.value ===
                                        deliveryformData.pricingPeriod
                                    ) || null
                                  }
                                  onChange={e => {
                                    handleSelectChangeDelivery(
                                      e,
                                      'pricingPeriod'
                                    );
                                    setErrors(prev => ({
                                      ...prev,
                                      pricingPeriod: '',
                                    }));
                                  }}
                                />
                              </div>
                              {errors.pricingPeriod && (
                                <div className='text-danger d-flex align-items-center gap-1'>
                                  <i className='mdi mdi-alert-circle-outline'></i>
                                  <span>{errors.pricingPeriod}</span>
                                </div>
                              )}{' '}
                            </Col>

                            {deliveryformData.pricingPeriod !== 3 && (
                              <Col sm={4}>
                                <div className='mb-2'>
                                  <Label className='form-label '>
                                    Index Price Reference
                                  </Label>
                                  <Select
                                    name='indexPriceId'
                                    id='indexPriceId'
                                    options={dataindexprice.items}
                                    placeholder='Please Select'
                                    isClearable
                                    value={
                                      dataindexprice.items?.find(
                                        obj =>
                                          obj.value ===
                                          deliveryformData.indexPriceId
                                      ) || null
                                    }
                                    onChange={e => {
                                      handleSelectChangeDelivery(
                                        e,
                                        'indexPriceId'
                                      );
                                      setErrors(prev => ({
                                        ...prev,
                                        indexPriceId: '',
                                      }));
                                    }}
                                  />
                                </div>
                                {errors.indexPriceId && (
                                  <div className='text-danger d-flex align-items-center gap-1'>
                                    <i className='mdi mdi-alert-circle-outline'></i>
                                    <span>{errors.indexPriceId}</span>
                                  </div>
                                )}{' '}
                              </Col>
                            )}
                            {deliveryformData.pricingPeriod !== 3 && (
                              <Col sm={4}>
                                <div className='mb-2'>
                                  <Label className='form-label '>
                                    Other Index Price
                                  </Label>
                                  <Select
                                    name='otherIndexPriceId'
                                    id='otherIndexPriceId'
                                    options={dataindexprice.items}
                                    placeholder='Please Select'
                                    isClearable
                                    value={
                                      dataindexprice.items?.find(
                                        obj =>
                                          obj.value ===
                                          deliveryformData.otherIndexPriceId
                                      ) || null
                                    }
                                    onChange={e => {
                                      handleSelectChangeDelivery(
                                        e,
                                        'otherIndexPriceId'
                                      );
                                      setErrors(prev => ({
                                        ...prev,
                                        otherIndexPriceId: '',
                                      }));
                                    }}
                                  />
                                </div>
                                {errors.otherIndexPriceId && (
                                  <div className='text-danger d-flex align-items-center gap-1'>
                                    <i className='mdi mdi-alert-circle-outline'></i>
                                    <span>{errors.otherIndexPriceId}</span>
                                  </div>
                                )}{' '}
                              </Col>
                            )}
                            {deliveryformData.pricingPeriod === 1 && (
                              <Col sm={2}>
                                <div className='mb-2'>
                                  <Label
                                    for='startDatePublishIndex'
                                    className='form-label '
                                  >
                                    Publish Index Period :
                                  </Label>
                                  <Input
                                    type='date'
                                    id='startDatePublishIndex'
                                    name='startDatePublishIndex'
                                    value={getSafeInputDate(
                                      deliveryformData.startDatePublishIndex,
                                      convertToInputDateFormat
                                    )}
                                    className='form-control'
                                    onChange={e => {
                                      handleDeliveryDateChange(e);
                                      const newVal = convertToDisplayDateFormat(
                                        e.target.value
                                      );
                                      const err = validatePublishIndexDates(
                                        newVal,
                                        deliveryformData.endDatePublishIndex
                                      );
                                      setErrors(prev => ({
                                        ...prev,
                                        endDatePublishIndex: err,
                                        startDatePublishIndex: '',
                                      }));
                                    }}
                                  />
                                  {errors.startDatePublishIndex && (
                                    <div className='text-danger d-flex align-items-center gap-1'>
                                      <i className='mdi mdi-alert-circle-outline'></i>
                                      <span>
                                        {errors.startDatePublishIndex}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </Col>
                            )}

                            {deliveryformData.pricingPeriod === 1 && (
                              <Col sm={2}>
                                <div className='mb-2'>
                                  <Label
                                    for='endDatePublishIndex'
                                    className='form-label '
                                  >
                                    To :
                                  </Label>

                                  <Input
                                    type='date'
                                    id='endDatePublishIndex'
                                    name='endDatePublishIndex'
                                    value={getSafeInputDate(
                                      deliveryformData.endDatePublishIndex,
                                      convertToInputDateFormat
                                    )}
                                    className='form-control'
                                    onChange={e => {
                                      handleDeliveryDateChange(e);
                                      const newVal = convertToDisplayDateFormat(
                                        e.target.value
                                      );
                                      const err = validatePublishIndexDates(
                                        deliveryformData.startDatePublishIndex,
                                        newVal
                                      );
                                      setErrors(prev => ({
                                        ...prev,
                                        endDatePublishIndex: err,
                                      }));
                                    }}
                                  />
                                  {errors.endDatePublishIndex && (
                                    <div className='text-danger d-flex align-items-center gap-1'>
                                      <i className='mdi mdi-alert-circle-outline'></i>
                                      <span>{errors.endDatePublishIndex}</span>
                                    </div>
                                  )}
                                </div>
                              </Col>
                            )}

                            {deliveryformData.pricingPeriod === 2 && (
                              <Col sm={4}>
                                <div className='mb-2'>
                                  <Label for='schemaBL' className='form-label '>
                                    Schema B/L Date
                                  </Label>
                                  <Input
                                    type='text'
                                    id='schemaBL'
                                    name='schemaBL'
                                    value={deliveryformData.schemaBL || ''}
                                    className='form-control'
                                    onChange={e => {
                                      handleDeliveryTextChange(e);
                                      setErrors(prev => ({
                                        ...prev,
                                        schemaBL: '',
                                      }));
                                    }}
                                  />
                                  {errors.schemaBL && (
                                    <div className='text-danger d-flex align-items-center gap-1'>
                                      <i className='mdi mdi-alert-circle-outline'></i>
                                      <span>{errors.schemaBL}</span>
                                    </div>
                                  )}
                                </div>
                              </Col>
                            )}

                            {/* {deliveryformData.pricingPeriod === 3 && (
                              <Col sm={4}>
                                <div className='mb-2'>
                                  <Label
                                    for='priceAmount'
                                    className='form-label '
                                  >
                                    Price Amount
                                  </Label>

                                  <div className='input-group'>
                                    <Cleave
                                      placeholder='Enter numeral'
                                      id='priceAmount'
                                      name='priceAmount'
                                      value={deliveryformData.priceAmount || ''}
                                      options={{
                                        numeral: true,
                                        numeralThousandsGroupStyle: 'thousand',
                                      }}
                                      onChange={e => {
                                        handleDeliveryTextChange(e);
                                      }}
                                      // onChange={onNumberChange('priceAmount')}
                                      readOnly={
                                        form.isProcurementCompleted !== false
                                      }
                                      className={
                                        form.isProcurementCompleted === false
                                          ? 'form-control'
                                          : 'form-control bg-light border-1'
                                      }
                                    />

                                    <span
                                      className='input-group-text'
                                      id='basic-addon2'
                                    >
                                      USD/bbl
                                    </span>
                                  </div>
                                </div>
                              </Col>
                            )} */}

                            {deliveryformData.pricingPeriod !== 3 && (
                              <Col sm={4}>
                                <div className='mb-2'>
                                  <Label for='schemaBL' className='form-label '>
                                    {''}
                                  </Label>
                                  <Input
                                    type='text'
                                    id='isCorrelationBetweenTwoIndexRO'
                                    name='isCorrelationBetweenTwoIndexRO'
                                    readOnly
                                    value={
                                      (deliveryformData.isCorrelationBetweenTwoIndex &&
                                        'Correlation between two index whicever is lower') ||
                                      ''
                                    }
                                    className='form-control bg-light border-0'
                                  />
                                </div>
                              </Col>
                            )}
                          </Row>
                        </Col>
                      </Row>
                    </Col>

                    <Col sm={12}>
                      <Row>
                        <Col sm={12}>
                          <Row>
                            <Col sm={12}>
                              <div className='mb-2'>
                                <Label className='form-check-label'>
                                  Notes for Pricing
                                </Label>
                              </div>

                              <div className='snow-editor '>
                                <EditorProvider>
                                  <Editor
                                    id='notesForPricing'
                                    name='notesForPricing'
                                    value={
                                      deliveryformData.notesForPricing || ''
                                    }
                                    onChange={handleTextChangeDelivery(
                                      'notesForPricing'
                                    )}
                                    style={{
                                      height: '200px',
                                      overflowY: 'auto',
                                    }}
                                    className={
                                      form.isProcurementCompleted === false
                                        ? 'form-control'
                                        : 'form-control bg-light border-0'
                                    }
                                  >
                                    {form.isProcurementCompleted === false && (
                                      <Toolbar>
                                        <BtnBold />
                                        <BtnItalic />
                                        <BtnBulletList />
                                        <BtnClearFormatting />
                                        <BtnLink />
                                        <BtnNumberedList />
                                        <BtnRedo />
                                        <BtnStrikeThrough />
                                        <BtnStyles />
                                        <BtnUnderline />
                                        <BtnUndo />
                                        <Separator />
                                      </Toolbar>
                                    )}
                                  </Editor>
                                </EditorProvider>
                              </div>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </Col>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Col>
          </Row>
        </ModalBody>

        <ModalFooter>
          <button
            type='button'
            className='btn btn-primary btn-label right  nexttab'
            onClick={() => {
              setModal(!modal);
            }}
          >
            <i className=' ri-logout-box-r-line label-icon align-middle fs-16 ms-2'></i>
            Close
          </button>
          {form.isProcurementCompleted === false && (
            <button
              type='button'
              className='btn btn-primary btn-label right  nexttab'
              onClick={() => {
                //setModal(!modal);
                deliveryformData.id !== null
                  ? handleUpdDataDelivery()
                  : handleAddDataDelivery();
              }}
            >
              <i className=' ri-database-2-line label-icon align-middle fs-16 ms-2'></i>
              {deliveryformData.id !== null ? 'Update' : 'Save'}
            </button>
          )}
        </ModalFooter>
      </Modal>
      <Modal
        isOpen={modalincoterm}
        role='dialog'
        autoFocus={true}
        centered
        id='addAddressModal'
        toggle={togglemodalincoterm}
        data-bs-backdrop='static'
        size='lg'
      >
        <ModalHeader
          toggle={() => {
            setModalIncoterm(!modalincoterm);
          }}
        >
          <span className='modal-title' id='addAddressModalLabel'>
            Incoterm :
          </span>
        </ModalHeader>

        <ModalBody>
          <div className='d-flex mt-2'>
            <Col sm={12} style={{ minHeight: '400px' }}>
              <Row>
                <Col sm={4}>
                  <div className='mb-2'>
                    <Label className='form-label '>Type :</Label>
                    <Select
                      name='incotermId'
                      id='incotermId'
                      options={incotermtype.items}
                      placeholder='Please Select'
                      value={
                        incotermtype.items?.find(
                          obj =>
                            obj.value === deliveryincotermformData.incotermId
                        ) || null
                      }
                      onChange={e => {
                        handleSelectChangeIncoterm(e, 'incotermId');
                      }}
                    />
                    {incotermErrors.incotermId && (
                      <div className='text-danger d-flex align-items-center gap-1 mt-1'>
                        <i className='mdi mdi-alert-circle-outline'></i>
                        <span>{incotermErrors.incotermId}</span>
                      </div>
                    )}
                  </div>
                </Col>

                {deliveryincotermformData.incotermId !==
                  'e0173dfd-e109-4054-8640-23d95befd326' && (
                  <Col sm={4}>
                    <div className='mb-2'>
                      <Label className='form-label '>Port(s) :</Label>
                      <Select
                        id='incotermPortType'
                        name='incotermPortType'
                        options={IncotermPortTypeData}
                        placeholder='Please Select'
                        value={
                          IncotermPortTypeData[0].options.find(
                            option =>
                              option.value ===
                              deliveryincotermformData.incotermPortType
                          ) || null
                        }
                        onChange={e => {
                          handleSelectChangeIncoterm(e, 'incotermPortType');
                        }}
                      />
                      {incotermErrors.incotermPortType && (
                        <div className='text-danger d-flex align-items-center gap-1 mt-1'>
                          <i className='mdi mdi-alert-circle-outline'></i>
                          <span>{incotermErrors.incotermPortType}</span>
                        </div>
                      )}
                    </div>
                  </Col>
                )}

                {deliveryincotermformData.incotermId ===
                  'e0173dfd-e109-4054-8640-23d95befd326' && (
                  <Col sm={4}>
                    <div className='mb-2'>
                      <Label className='form-label '>Route :</Label>

                      <Select
                        name='incotermRouteId'
                        id='incotermRouteId'
                        options={
                          IncotermRouteData.items?.filter(
                            item => item.label !== 'N/A'
                          ) || []
                        }
                        placeholder='Please Select'
                        value={
                          IncotermRouteData.items
                            ?.filter(item => item.label !== 'N/A')
                            ?.find(
                              obj =>
                                obj.value ===
                                deliveryincotermformData.incotermRouteId
                            ) || null
                        }
                        onChange={e => {
                          handleSelectChangeIncoterm(e, 'incotermRouteId');
                        }}
                      />
                      {incotermErrors.incotermRouteId && (
                        <div className='text-danger d-flex align-items-center gap-1 mt-1'>
                          <i className='mdi mdi-alert-circle-outline'></i>
                          <span>{incotermErrors.incotermRouteId}</span>
                        </div>
                      )}
                    </div>
                  </Col>
                )}

                {deliveryincotermformData.incotermId !==
                  'e0173dfd-e109-4054-8640-23d95befd326' &&
                  deliveryincotermformData.incotermPortType === 'MultiPort' && (
                    <Col sm={4}>
                      <div className='mb-2'>
                        <Label className='form-label '>Multi Port(s) :</Label>
                        <Select
                          id='incotermMultiPortType'
                          name='incotermMultiPortType'
                          options={IncotermMultiPortTypeData}
                          placeholder='Please Select'
                          value={
                            IncotermMultiPortTypeData[0].options.find(
                              option =>
                                option.value ===
                                deliveryincotermformData.incotermMultiPortType
                            ) || null
                          }
                          onChange={e => {
                            handleSelectChangeIncoterm(
                              e,
                              'incotermMultiPortType'
                            );
                          }}
                        />
                      </div>
                    </Col>
                  )}
              </Row>

              {/* Location 1 */}
              <div
                className='mb-2 mt-2'
                style={{
                  borderTopColor: '#b0b8b2',
                  borderTopWidth: '1px',
                  borderTopStyle: 'solid',
                }}
              ></div>

              <Row className='mb-3 '>
                <Col sm={3}>
                  <Label className='form-label'>Total Quantity</Label>
                  {(deliveryincotermformData.id === null
                    ? qtyDeliveryDataTotalQty.items
                    : qtyDeliveryDataQuantity.items
                  )
                    // ): qtyDeliveryDataQuantity.items

                    .map((data, index) => (
                      <Row key={`${data.id}-${index}`}>
                        <Col sm={12}>
                          <div className='input-group mb-1'>
                            <Input
                              type='text'
                              className='form-control bg-light '
                              value={formatNumberUS(data.quantity) || ''}
                              readOnly
                            />
                            <span
                              className='input-group-text bg-light '
                              id='basic-addon2'
                            >
                              {unitSizeType}
                            </span>
                          </div>
                        </Col>
                      </Row>
                    ))}
                </Col>
                <Col sm={9}>
                  {deliveryincotermformData.incotermId !== null && (
                    <Row>
                      <Col sm={12}>
                        <Row>
                          {deliveryincotermformData.incotermId !==
                          'e0173dfd-e109-4054-8640-23d95befd326' ? (
                            <Col sm={4}>
                              <div className='mb-2'>
                                <Label className='form-label '>
                                  {deliveryincotermformData.incotermMultiPortType ===
                                    '2Port' ||
                                  deliveryincotermformData.incotermMultiPortType ===
                                    '3Port'
                                    ? 'Location 1:'
                                    : 'Location :'}
                                </Label>

                                <Select
                                  name='incotermLocationId'
                                  id='incotermLocationId'
                                  options={IncotermLocationData.items}
                                  placeholder='Please Select'
                                  value={
                                    IncotermLocationData.items?.find(
                                      obj =>
                                        obj.value ===
                                        deliveryincotermformData.incotermLocationId
                                    ) || null
                                  }
                                  onChange={e => {
                                    handleSelectChangeIncoterm(
                                      e,
                                      'incotermLocationId'
                                    );
                                  }}
                                />
                                {incotermErrors.incotermLocationId && (
                                  <div className='text-danger d-flex align-items-center gap-1 mt-1'>
                                    <i className='mdi mdi-alert-circle-outline'></i>
                                    <span>
                                      {incotermErrors.incotermLocationId}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </Col>
                          ) : null}

                          {deliveryincotermformData.incotermId ===
                          'e0173dfd-e109-4054-8640-23d95befd326' ? (
                            <Col sm={4}>
                              <div className='mb-2'>
                                <Label className='form-label '>
                                  {'Location :'}
                                </Label>

                                <MultiSelect
                                  options={IncotermLocationData.items || []}
                                  name='incotermFOB'
                                  id='incotermFOB'
                                  labelledBy='Please Select'
                                  value={
                                    IncotermLocationData.items?.filter(obj => {
                                      const ids =
                                        deliveryincotermformData.incotermFOB;
                                      return Array.isArray(ids)
                                        ? ids.includes(obj.value)
                                        : ids === obj.value;
                                    }) || []
                                  }
                                  onChange={selectedItems => {
                                    const value =
                                      selectedItems?.map(item => item.value) ||
                                      [];
                                    setDeliveryIncotermFormData(prev => ({
                                      ...prev,
                                      incotermFOB: value,
                                    }));
                                  }}
                                />
                              </div>
                            </Col>
                          ) : null}

                          {(deliveryincotermformData.incotermMultiPortType ===
                            '2Port' ||
                            deliveryincotermformData.incotermMultiPortType ===
                              '3Port') &&
                          deliveryincotermformData.incotermId !==
                            'e0173dfd-e109-4054-8640-23d95befd326' &&
                          deliveryincotermformData.incotermPortType ===
                            'MultiPort' ? (
                            <Col sm={4}>
                              <div className='mb-2'>
                                <Label className='form-label '>
                                  Location 2 :{' '}
                                </Label>
                                <Select
                                  name='incotermLocation2Id'
                                  id='incotermLocation2Id'
                                  options={IncotermLocationData.items}
                                  placeholder='Please Select'
                                  value={
                                    IncotermLocationData.items?.find(
                                      obj =>
                                        obj.value ===
                                        deliveryincotermformData.incotermLocation2Id
                                    ) || null
                                  }
                                  onChange={e => {
                                    handleSelectChangeIncoterm(
                                      e,
                                      'incotermLocation2Id'
                                    );
                                  }}
                                />
                                {incotermErrors.incotermLocation2Id && (
                                  <div className='text-danger d-flex align-items-center gap-1 mt-1'>
                                    <i className='mdi mdi-alert-circle-outline'></i>
                                    <span>
                                      {incotermErrors.incotermLocation2Id}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </Col>
                          ) : null}

                          {deliveryincotermformData.incotermMultiPortType ===
                            '3Port' &&
                            deliveryincotermformData.incotermId !==
                              'e0173dfd-e109-4054-8640-23d95befd326' &&
                            deliveryincotermformData.incotermPortType ===
                              'MultiPort' && (
                              <Col sm={4}>
                                <div className='mb-2'>
                                  <Label className='form-label '>
                                    Location 3 :{' '}
                                  </Label>
                                  <Select
                                    name='incotermLocation3Id'
                                    id='incotermLocation3Id'
                                    options={IncotermLocationData.items}
                                    placeholder='Please Select'
                                    value={
                                      IncotermLocationData.items?.find(
                                        obj =>
                                          obj.value ===
                                          deliveryincotermformData.incotermLocation3Id
                                      ) || null
                                    }
                                    onChange={e => {
                                      handleSelectChangeIncoterm(
                                        e,
                                        'incotermLocation3Id'
                                      );
                                    }}
                                  />
                                  {incotermErrors.incotermLocation3Id && (
                                    <div className='text-danger d-flex align-items-center gap-1 mt-1'>
                                      <i className='mdi mdi-alert-circle-outline'></i>
                                      <span>
                                        {incotermErrors.incotermLocation3Id}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </Col>
                            )}
                        </Row>
                      </Col>

                      {(deliveryincotermformData.incotermId ===
                        'e0173dfd-e109-4054-8640-23d95befd326' ||
                        deliveryincotermformData.incotermId ===
                          'e0173dfd-e109-4054-8640-23d95befd327' ||
                        deliveryincotermformData.incotermId ===
                          'e0173dfd-e109-4054-8640-23d95befd328' ||
                        deliveryincotermformData.incotermId ===
                          'e0173dfd-e109-4054-8640-23d95befd329') && (
                        //deliveryincotermformData.incotermPortType ===
                        //'MultiPort' && (
                        // deliveryincotermformData.id &&
                        <Col sm={12}>
                          {(deliveryincotermformData.id === null
                            ? qtyDeliveryDataTotalQty.items
                            : qtyDeliveryDataQuantity.items
                          ).map((data, index) => (
                            <Row key={`${data.id}-${index}`}>
                              {(deliveryincotermformData.incotermMultiPortType ===
                                '2Port' ||
                                deliveryincotermformData.incotermMultiPortType ===
                                  '3Port') &&
                              deliveryincotermformData.incotermId !==
                                'e0173dfd-e109-4054-8640-23d95befd326' &&
                              deliveryincotermformData.incotermPortType ===
                                'MultiPort' ? (
                                <Col sm={4}>
                                  <div className='input-group mb-1'>
                                    <Input
                                      type='text'
                                      className='form-control'
                                      value={data.quantityLocation1 || ''}
                                      onChange={e =>
                                        handleQuantityChange(
                                          index,
                                          'quantityLocation1',
                                          e.target.value
                                        )
                                      }
                                    />
                                    <span
                                      className='input-group-text'
                                      id='basic-addon2'
                                    >
                                      {unitSizeType}
                                    </span>
                                  </div>
                                  {incotermErrors[
                                    `quantityLocation1_${index}`
                                  ] && (
                                    <div className='text-danger d-flex align-items-center gap-1 mt-1'>
                                      <i className='mdi mdi-alert-circle-outline'></i>
                                      <span>
                                        {
                                          incotermErrors[
                                            `quantityLocation1_${index}`
                                          ]
                                        }
                                      </span>
                                    </div>
                                  )}
                                </Col>
                              ) : null}
                              {(deliveryincotermformData.incotermMultiPortType ===
                                '2Port' ||
                                deliveryincotermformData.incotermMultiPortType ===
                                  '3Port') &&
                              deliveryincotermformData.incotermId !==
                                'e0173dfd-e109-4054-8640-23d95befd326' &&
                              deliveryincotermformData.incotermPortType ===
                                'MultiPort' ? (
                                <Col sm={4}>
                                  <div className='input-group'>
                                    <Input
                                      type='text'
                                      className='form-control '
                                      value={data.quantityLocation2 || ''}
                                      onChange={e =>
                                        handleQuantityChange(
                                          index,
                                          'quantityLocation2',
                                          e.target.value,
                                          2
                                        )
                                      }
                                    />
                                    <span
                                      className='input-group-text '
                                      id='basic-addon2'
                                    >
                                      {unitSizeType}
                                    </span>
                                  </div>
                                  {incotermErrors[
                                    `quantityLocation2_${index}`
                                  ] && (
                                    <div className='text-danger d-flex align-items-center gap-1 mt-1'>
                                      <i className='mdi mdi-alert-circle-outline'></i>
                                      <span>
                                        {
                                          incotermErrors[
                                            `quantityLocation2_${index}`
                                          ]
                                        }
                                      </span>
                                    </div>
                                  )}
                                </Col>
                              ) : null}

                              {deliveryincotermformData.incotermMultiPortType ===
                                '3Port' &&
                                deliveryincotermformData.incotermId !==
                                  'e0173dfd-e109-4054-8640-23d95befd326' &&
                                deliveryincotermformData.incotermPortType ===
                                  'MultiPort' && (
                                  <Col sm={4}>
                                    <div className='input-group'>
                                      <Input
                                        type='text'
                                        className='form-control '
                                        value={data.quantityLocation3 || ''}
                                        onChange={e =>
                                          handleQuantityChange(
                                            index,
                                            'quantityLocation3',
                                            e.target.value,
                                            3
                                          )
                                        }
                                      />
                                      <span
                                        className='input-group-text '
                                        id='basic-addon2'
                                      >
                                        {unitSizeType}
                                      </span>
                                    </div>
                                    {incotermErrors[
                                      `quantityLocation3_${index}`
                                    ] && (
                                      <div className='text-danger d-flex align-items-center gap-1 mt-1'>
                                        <i className='mdi mdi-alert-circle-outline'></i>
                                        <span>
                                          {
                                            incotermErrors[
                                              `quantityLocation3_${index}`
                                            ]
                                          }
                                        </span>
                                      </div>
                                    )}
                                  </Col>
                                )}
                            </Row>
                          ))}
                        </Col>
                      )}

                      <Col sm={4}>
                        <Row>
                          <Col sm={12}>
                            <div className='mb-2'>
                              <Label for='startDate' className='form-label '>
                                Date From:
                              </Label>

                              <Input
                                type='date'
                                id='startDate'
                                name='startDate'
                                value={convertToInputDateFormat(
                                  deliveryincotermformData.startDate
                                )}
                                className='form-control'
                                onChange={e => {
                                  handleDeliveryDateChangeIncoterm(e);
                                }}
                              />
                              {incotermErrors.startDate && (
                                <div className='text-danger d-flex align-items-center gap-1 mt-1'>
                                  <i className='mdi mdi-alert-circle-outline'></i>
                                  <span>{incotermErrors.startDate}</span>
                                </div>
                              )}
                            </div>
                          </Col>

                          <Col sm={12}>
                            <div className='mb-2'>
                              <Label for='endDate' className='form-label '>
                                Date to :
                              </Label>

                              <Input
                                type='date'
                                id='endDate'
                                name='endDate'
                                value={convertToInputDateFormat(
                                  deliveryincotermformData.endDate
                                )}
                                className='form-control'
                                onChange={e => {
                                  handleDeliveryDateChangeIncoterm(e);
                                }}
                              />
                              {incotermErrors.endDate && (
                                <div className='text-danger d-flex align-items-center gap-1 mt-1'>
                                  <i className='mdi mdi-alert-circle-outline'></i>
                                  <span>{incotermErrors.endDate}</span>
                                </div>
                              )}
                            </div>
                          </Col>
                        </Row>
                      </Col>
                      {(deliveryincotermformData.incotermMultiPortType ===
                        '2Port' ||
                        deliveryincotermformData.incotermMultiPortType ===
                          '3Port') &&
                      deliveryincotermformData.incotermId !==
                        'e0173dfd-e109-4054-8640-23d95befd326' &&
                      deliveryincotermformData.incotermPortType ===
                        'MultiPort' ? (
                        <Col sm={4}>
                          <Row>
                            <Col sm={12}>
                              <div className='mb-2'>
                                <Label className='form-label '>
                                  Date From :
                                </Label>
                                <Input
                                  type='date'
                                  id='startDate2'
                                  name='startDate2'
                                  value={convertToInputDateFormat(
                                    deliveryincotermformData.startDate2
                                  )}
                                  className='form-control'
                                  onChange={e => {
                                    handleDeliveryDateChangeIncoterm(e);
                                  }}
                                />
                                {incotermErrors.startDate2 && (
                                  <div className='text-danger d-flex align-items-center gap-1 mt-1'>
                                    <i className='mdi mdi-alert-circle-outline'></i>
                                    <span>{incotermErrors.startDate2}</span>
                                  </div>
                                )}
                              </div>
                            </Col>

                            <Col sm={12}>
                              <div className='mb-2'>
                                <Label for='endDate2' className='form-label '>
                                  Date to :
                                </Label>

                                <Input
                                  type='date'
                                  id='endDate2'
                                  name='endDate2'
                                  value={convertToInputDateFormat(
                                    deliveryincotermformData.endDate2
                                  )}
                                  className='form-control'
                                  onChange={e => {
                                    handleDeliveryDateChangeIncoterm(e);
                                  }}
                                />
                                {incotermErrors.endDate2 && (
                                  <div className='text-danger d-flex align-items-center gap-1 mt-1'>
                                    <i className='mdi mdi-alert-circle-outline'></i>
                                    <span>{incotermErrors.endDate2}</span>
                                  </div>
                                )}
                              </div>
                            </Col>
                          </Row>
                        </Col>
                      ) : null}

                      {deliveryincotermformData.incotermMultiPortType ===
                        '3Port' &&
                        deliveryincotermformData.incotermId !==
                          'e0173dfd-e109-4054-8640-23d95befd326' &&
                        deliveryincotermformData.incotermPortType ===
                          'MultiPort' && (
                          <Col sm={4}>
                            <Row>
                              <Col sm={12}>
                                <div className='mb-2'>
                                  <Label className='form-label '>
                                    Date From :
                                  </Label>

                                  <Input
                                    type='date'
                                    id='startDate3'
                                    name='startDate3'
                                    value={convertToInputDateFormat(
                                      deliveryincotermformData.startDate3
                                    )}
                                    className='form-control'
                                    onChange={e => {
                                      handleDeliveryDateChangeIncoterm(e);
                                    }}
                                  />
                                  {incotermErrors.startDate3 && (
                                    <div className='text-danger d-flex align-items-center gap-1 mt-1'>
                                      <i className='mdi mdi-alert-circle-outline'></i>
                                      <span>{incotermErrors.startDate3}</span>
                                    </div>
                                  )}
                                </div>
                              </Col>

                              <Col sm={12}>
                                <div className='mb-2'>
                                  <Label for='endDate3' className='form-label '>
                                    Date to :
                                  </Label>

                                  <Input
                                    type='date'
                                    id='endDate3'
                                    name='endDate3'
                                    value={convertToInputDateFormat(
                                      deliveryincotermformData.endDate3
                                    )}
                                    className='form-control'
                                    onChange={e => {
                                      handleDeliveryDateChangeIncoterm(e);
                                    }}
                                  />
                                  {incotermErrors.endDate3 && (
                                    <div className='text-danger d-flex align-items-center gap-1 mt-1'>
                                      <i className='mdi mdi-alert-circle-outline'></i>
                                      <span>{incotermErrors.endDate3}</span>
                                    </div>
                                  )}
                                </div>
                              </Col>
                            </Row>
                          </Col>
                        )}
                    </Row>
                  )}
                </Col>
              </Row>

              {deliveryincotermformData.incotermId !== null && (
                <Row>
                  <Col sm={8}>
                    <Row>
                      <Col sm={12}>
                        <div className='mb-2'>
                          <Label className='form-label '>Timeline :</Label>

                          <Input
                            type='text'
                            id='timelineType'
                            name='timelineType'
                            className='form-control bg-light '
                            value={
                              deliveryincotermformData.timelineType === 'ALD'
                                ? 'Accepted Loading Dates'
                                : 'Accepted Discharged Dates'
                            }
                            readOnly
                          />
                        </div>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              )}
            </Col>
          </div>
        </ModalBody>

        <ModalFooter>
          <button
            type='button'
            className='btn btn-primary btn-label right  nexttab'
            onClick={() => {
              setModalIncoterm(!modalincoterm);
            }}
          >
            <i className=' ri-logout-box-r-line label-icon align-middle fs-16 ms-2'></i>
            Close
          </button>

          <button
            type='button'
            className='btn btn-primary btn-label right  nexttab'
            onClick={() => {
              deliveryincotermformData.id === null
                ? handleAddDataDeliveryIncoterm()
                : handleUpdDataDeliveryIncoterm();
            }}
          >
            <i className=' ri-database-2-line label-icon align-middle fs-16 ms-2'></i>
            {deliveryincotermformData.id === null ? 'Save' : 'Update'}
          </button>
        </ModalFooter>
      </Modal>
      <Modal
        isOpen={modalquantity}
        role='dialog'
        autoFocus={true}
        centered
        id='addAddressModal'
        toggle={togglemodalquantity}
        data-bs-backdrop='static'
        size='sm'
      >
        <ModalBody>
          <div className='d-flex mt-2'>
            <Col sm={12}>
              <Row>
                <Col sm={12}>
                  <div className='mb-1'>
                    <Label className='form-label '>Quantity :</Label>

                    <div className='input-group'>
                      <Input
                        type='number'
                        id='quantity'
                        name='quantity'
                        className='form-control'
                        value={deliveryquantityformData.quantity || ''}
                        onChange={e => {
                          const { name, value, type } = e.target;
                          const newValue =
                            type === 'number'
                              ? value === ''
                                ? ''
                                : parseFloat(value)
                              : value;
                          setDeliveryQuantityFormData({
                            ...deliveryquantityformData,
                            [name]: newValue,
                          });
                        }}
                      />
                      <span className='input-group-text' id='basic-addon2'>
                        {unitSizeType}
                      </span>
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            color='primary'
            className='btn-label btn-sm right'
            onClick={() => {
              setModalQuantity(!modalquantity);
              deliveryquantityformData.id !== null
                ? handleUpdDataDeliveryQuantity(deliveryquantityformData.id)
                : handleAddDataDeliveryQuantity();
            }}
          >
            {' '}
            <i className='ri-database-2-line label-icon align-middle fs-16 ms-2'></i>{' '}
            {deliveryquantityformData.id !== null ? 'Update' : 'Save'}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={modalloadingdischarge}
        role='dialog'
        autoFocus={true}
        centered
        id='addAddressModal'
        toggle={toggleloadingdischrge}
        data-bs-backdrop='static'
        size='xl'
      >
        <ModalHeader
          toggle={() => {
            setModalLoadingDischarge(!modalloadingdischarge);
          }}
        >
          <div className='flex-grow-1'>
            <Label className='form-label  text-uppercase fw-semibold'>
              Loading / Discharge Port/s
            </Label>
          </div>
        </ModalHeader>

        <ModalBody>
          <div className='d-flex mt-1'>
            <Col sm={12}>
              {!deliveryincotermformData.incotermLocation?.includes('STS') && (
                <DeliveryVesselFirstLocation
                  form={form}
                  deliveryincotermformData={deliveryincotermformData}
                  handleDeliveryNumberChangeIncoterm={
                    handleDeliveryNumberChangeIncoterm
                  }
                />
              )}

              {!deliveryincotermformData.incotermLocation2?.includes('STS') &&
                (deliveryincotermformData.incotermMultiPortType === '2Port' ||
                  deliveryincotermformData.incotermMultiPortType === '3Port') &&
                deliveryincotermformData.incotermId !==
                  'e0173dfd-e109-4054-8640-23d95befd326' && (
                  <DeliveryVesselSecondLocation
                    form={form}
                    deliveryincotermformData={deliveryincotermformData}
                    handleDeliveryNumberChangeIncoterm={
                      handleDeliveryNumberChangeIncoterm
                    }
                  />
                )}

              {deliveryincotermformData.incotermLocation?.includes('STS') && (
                <DeliveryVesselFirstLocationNotes
                  form={form}
                  deliveryincotermformData={deliveryincotermformData}
                  handleDeliveryTextChangeIncoterm={
                    handleDeliveryTextChangeIncoterm
                  }
                />
              )}

              <Row>
                <Col sm={12}>
                  <div className='mb-1  '>
                    <Label
                      for='additionalInformation'
                      className='form-label  text-uppercase fw-semibold'
                    >
                      Notes for Loading / Discharged Port
                    </Label>
                  </div>
                </Col>

                <Col sm={12}>
                  <Card>
                    <div className='snow-editor '>
                      <EditorProvider>
                        <Editor
                          id='additionalInformation'
                          name='additionalInformation'
                          value={
                            deliveryincotermformData.additionalInformation || ''
                          }
                          onChange={e => {
                            handleNoteTextChange(e, 'additionalInformation');
                          }}
                          style={{ height: '150px', overflowY: 'auto' }}
                          className={
                            form.isProcurementCompleted === false
                              ? 'form-control'
                              : 'form-control bg-light border-0'
                          }
                        >
                          {form.isProcurementCompleted === false && (
                            <Toolbar>
                              <BtnBold />
                              <BtnItalic />
                              <BtnBulletList />
                              <BtnClearFormatting />
                              <BtnLink />
                              <BtnNumberedList />
                              <BtnRedo />
                              <BtnStrikeThrough />
                              <BtnStyles />
                              <BtnUnderline />
                              <BtnUndo />

                              <Separator />
                            </Toolbar>
                          )}
                        </Editor>
                      </EditorProvider>
                    </div>
                  </Card>
                </Col>
              </Row>
            </Col>
          </div>
        </ModalBody>

        <ModalFooter>
          <button
            type='button'
            className='btn btn-primary btn-label right  nexttab'
            onClick={() => {
              setModalLoadingDischarge(!modalloadingdischarge);
            }}
          >
            <i className=' ri-logout-box-r-line label-icon align-middle fs-16 ms-2'></i>
            Close
          </button>
          {form.isProcurementCompleted === false && (
            <button
              type='button'
              className='btn btn-primary btn-label right  nexttab'
              onClick={() => {
                setModalLoadingDischarge(!modalloadingdischarge);
                deliveryformData.id !== null
                  ? handleUpdDataDeliveryIncoterm()
                  : handleAddDataDelivery();
              }}
            >
              <i className=' ri-database-2-line label-icon align-middle fs-16 ms-2'></i>
              {deliveryformData.id !== null ? 'Update' : 'Save'}
            </button>
          )}
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={modalquantityincoterm}
        role='dialog'
        autoFocus={true}
        centered
        id='addAddressModal'
        toggle={togglemodalquantityincoterm}
        data-bs-backdrop='static'
        size='sm'
      >
        <ModalBody>
          <div className='d-flex mt-2'>
            <Col sm={12}>
              <Row>
                <Col sm={12}>
                  <div className='mb-1'>
                    <Label className='form-label '>Location 1 :</Label>

                    <div className='input-group'>
                      <Input
                        type='number'
                        id='quantityLocation1'
                        name='quantityLocation1'
                        className='form-control'
                        value={deliveryquantityformData.quantityLocation1 || ''}
                        onChange={e => {
                          const { name, value, type } = e.target;
                          const newValue =
                            type === 'number'
                              ? value === ''
                                ? ''
                                : parseFloat(value)
                              : value;
                          setDeliveryQuantityFormData({
                            ...deliveryquantityformData,
                            [name]: newValue,
                          });
                        }}
                      />
                      <span className='input-group-text' id='basic-addon2'>
                        MT
                      </span>
                    </div>
                  </div>
                </Col>

                <Col sm={12}>
                  <div className='mb-1'>
                    <Label className='form-label '>Location 2 :</Label>

                    <div className='input-group'>
                      <Input
                        type='number'
                        id='quantityLocation2'
                        name='quantityLocation2'
                        className='form-control'
                        value={deliveryquantityformData.quantityLocation2 || ''}
                        onChange={e => {
                          const { name, value, type } = e.target;
                          const newValue =
                            type === 'number'
                              ? value === ''
                                ? ''
                                : parseFloat(value)
                              : value;
                          setDeliveryQuantityFormData({
                            ...deliveryquantityformData,
                            [name]: newValue,
                          });
                        }}
                      />
                      <span className='input-group-text' id='basic-addon2'>
                        {unitSizeType}
                      </span>
                    </div>
                  </div>
                </Col>

                <Col sm={12}>
                  <div className='mb-1'>
                    <Label className='form-label '>Total Quantity :</Label>

                    <div className='input-group'>
                      <Input
                        type='number'
                        id='quantity'
                        name='quantity'
                        className='form-control  bg-light border-0'
                        value={deliveryquantityformData.quantity || ''}
                        readOnly
                      />
                      <span
                        className='input-group-text bg-light border-0'
                        id='basic-addon2'
                      >
                        {unitSizeType}
                      </span>
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            color='primary'
            className='btn-label btn-sm right'
            onClick={() => {
              setModalQuantityIncoterm(!modalquantityincoterm);
              handleUpdDataDeliveryQuantity(deliveryquantityformData.id);
            }}
          >
            {' '}
            <i className='ri-database-2-line label-icon align-middle fs-16 ms-2'></i>{' '}
            Update
          </Button>
        </ModalFooter>
      </Modal>

      <ViewOriginModal
        isOpen={viewOriginModal}
        toggle={() => setViewOriginModal(false)}
        locations={viewOriginLocations}
        routes={viewOriginRoutes}
      />
    </React.Fragment>
  );
};

export default RequirementTab;
